import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { buildGuestHistory, type GuestHistoryEntry } from "@/lib/returning-guests";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getEmailAudience, AUDIENCE_LABEL } from "@/lib/email-audience";
import {
  type AdminBooking,
  type PaymentEvent,
  type AdminNotificationAuditRow,
  type Keykeeper,
  approveBookingWithQuote,
  checkIsAdmin,
  createBlock,
  deleteBooking,
  listBookings,
  listAdminNotificationAudit,
  listPaymentEvents,
  sendAdminTestNotification,
  sendPaymentLinkEmail,
  setManualPaymentStatus,
  previewPaymentLinkEmail,
  updateBooking,
  listKeykeepers,
  upsertKeykeeper,
  deleteKeykeeper,
  sendBookingToKeykeepers,
  resetAdminPassword,
  resendDepositReceivedEmail,
  listBookingEmails,
  resendBookingEmail,
  RESENDABLE_TEMPLATE_NAMES,
  type BookingEmailLogRow,
  listAuditLogs,
  type AuditLogRow,
} from "@/lib/admin.functions";
import {
  getPricingSettings,
  updatePricingSettings,
  type PricingSettings,
} from "@/lib/pricing.functions";
import { getGuestInfoForBooking, createGuestInfoSubmission } from "@/lib/guest-info.functions";
import {
  listManualCustomers,
  upsertManualCustomer,
  deleteManualCustomer,
  type ManualCustomer,
} from "@/lib/customers.functions";
import { rateCustomer } from "@/lib/customers.functions";
import {
  forceEmailQueueFlush,
  sendAllTemplatesTest,
  type ForceFlushResult,
  type TemplateTestResult,
} from "@/lib/email-queue.functions";
import { StatsPanel } from "@/components/admin/StatsPanel";
import { ConflictBanner } from "@/components/admin/ConflictBanner";
import { AccountPanel } from "@/components/admin/AccountPanel";
import { ContractPanel } from "@/components/admin/ContractPanel";
import DocumentsPanel from "@/components/admin/DocumentsPanel";
import GuestDocsPanel from "@/components/admin/GuestDocsPanel";
import RefundPanel from "@/components/admin/RefundPanel";
import RefundDossierSection from "@/components/admin/RefundDossier";
import { ReviewsPanel } from "@/components/admin/ReviewsPanel";
import { VideosPanel } from "@/components/admin/VideosPanel";
import { IcalSyncPanel } from "@/components/admin/IcalSyncPanel";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarHeader,
  SidebarFooter,
  SidebarTrigger,
  SidebarInset,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  CalendarRange,
  Users,
  BarChart3,
  Bell,
  KeyRound,
  Tags,
  ShieldCheck,
  Star,
  MoreHorizontal,
  Search,
  Plus,
  LogOut,
  AlertCircle,
  Wallet,
  PlaneLanding,
  CheckCircle2,
  CircleDashed,
  Hourglass,
  FileText,
  History,
  Ban,
  CalendarSync,
  ClipboardList,
  Banknote,
  Film,

} from "lucide-react";

function statusNL(s: string): string {
  switch (s) {
    case "pending": return "openstaand";
    case "confirmed": return "bevestigd";
    case "cancelled": return "geannuleerd";
    default: return s;
  }
}

function notifStatusNL(s: string): string {
  switch (s) {
    case "all": return "Alle";
    case "sent": return "Verzonden";
    case "queued": return "In wachtrij";
    case "failed": return "Mislukt";
    case "suppressed": return "Onderdrukt";
    default: return s;
  }
}

type PaymentState = "not_requested" | "link_sent" | "checkout_started" | "received";

function paymentStateOf(b: AdminBooking): PaymentState | null {
  if (b.kind !== "inquiry") return null;
  if (!b.approved_at) return null;
  // Booking-level summary: deposit drives the state until paid, then balance.
  if (!b.deposit_paid_at) {
    if (b.stripe_deposit_session_id) return "checkout_started";
    if (b.deposit_payment_token) return "link_sent";
    return "not_requested";
  }
  if (b.balance_paid_at) return "received";
  if (b.stripe_balance_session_id) return "checkout_started";
  if (b.balance_payment_token) return "link_sent";
  return "not_requested";
}

/** Echte boekingen (geen blokkades/annulaties) waarvan voorschot of saldo nog niet betaald is. */
function isUnpaidBooking(b: AdminBooking): boolean {
  if (b.kind !== "inquiry") return false;
  if (b.status === "cancelled") return false;
  return !b.deposit_paid_at || !b.balance_paid_at;
}

const PAYMENT_FILTER_LABELS: Record<PaymentState, string> = {
  not_requested: "Niet aangevraagd",
  link_sent: "Link verzonden",
  checkout_started: "Checkout gestart",
  received: "Ontvangen",
};

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Beheer — Casa Mi Pajobe" }, { name: "robots", content: "noindex" }] }),
  component: AdminPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-xl px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Kan beheerpaneel niet laden</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground">
          Opnieuw proberen
        </button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-12 text-center text-muted-foreground">Niet gevonden.</div>,
});

function AdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const checkAdmin = useServerFn(checkIsAdmin);
  const fetchList = useServerFn(listBookings);

  const adminQ = useQuery({ queryKey: ["is-admin"], queryFn: () => checkAdmin() });
  const listQ = useQuery({
    queryKey: ["admin-bookings"],
    queryFn: () => fetchList(),
    enabled: adminQ.data?.isAdmin === true,
  });

  const [filter, setFilter] = useState<
    "all" | "pending" | "confirmed" | "cancelled" | "flagged" | "unpaid"
  >("all");
  const [payFilter, setPayFilter] = useState<"all" | PaymentState>("all");
  const [editing, setEditing] = useState<AdminBooking | null>(null);
  const [quoting, setQuoting] = useState<AdminBooking | null>(null);
  const [detailing, setDetailing] = useState<AdminBooking | null>(null);
  const [guestInfoFor, setGuestInfoFor] = useState<AdminBooking | null>(null);
  const [blocking, setBlocking] = useState(false);
  const [tab, setTab] = useState<AdminTab>("dashboard");
  const [search, setSearch] = useState("");

  const isHeadAdmin = adminQ.data?.isHeadAdmin === true;
  useEffect(() => {
    if (tab === "account" && !isHeadAdmin) setTab("dashboard");
    if (tab === "activity" && !isHeadAdmin) setTab("dashboard");
    if (tab === "videos" && !isHeadAdmin) setTab("dashboard");
  }, [tab, isHeadAdmin]);

  async function handleSignOut() {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  if (adminQ.isLoading) {
    return <div className="p-12 text-center text-muted-foreground">Laden…</div>;
  }

  if (adminQ.data && !adminQ.data.isAdmin) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-2xl font-semibold">Geen toegang</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Je account bestaat maar heeft nog geen beheerdersrol. Vraag de eigenaar om toegang.
        </p>
        <button onClick={handleSignOut} className="mt-6 text-sm underline">Afmelden</button>
      </div>
    );
  }

  const allRows = listQ.data ?? [];
  const otherBlocks = allRows.filter(
    (b) => b.kind === "block" && b.name !== "Boeking (extern)",
  );
  const otherBlockIds = new Set(otherBlocks.map((b) => b.id));
  const bookings = allRows.filter((b) => !otherBlockIds.has(b.id));
  const searchLower = search.trim().toLowerCase();
  const filtered = bookings.filter((b) => {
    if (filter !== "all") {
      if (filter === "flagged" && !b.flagged) return false;
      if (filter === "unpaid" && !isUnpaidBooking(b)) return false;
      if (filter !== "flagged" && filter !== "unpaid" && b.status !== filter) return false;
    }
    if (payFilter !== "all") {
      if (paymentStateOf(b) !== payFilter) return false;
    }
    if (searchLower) {
      const hay = `${b.reference} ${b.name} ${b.email}`.toLowerCase();
      if (!hay.includes(searchLower)) return false;
    }
    return true;
  });

  const counts = {
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
    flagged: bookings.filter((b) => b.flagged).length,
    unpaid: bookings.filter((b) => isUnpaidBooking(b)).length,
  };


  const payCounts: Record<PaymentState, number> = {
    not_requested: 0, link_sent: 0, checkout_started: 0, received: 0,
  };
  for (const b of bookings) {
    const s = paymentStateOf(b);
    if (s) payCounts[s]++;
  }

  const tabTitles: Record<typeof tab, string> = {
    dashboard: "Dashboard",
    bookings: "Boekingen",
    blocks: "Blokkades",
    customers: "Huurders",
    stats: "Statistieken",
    notifications: "Meldingen",
    keykeepers: "Sleutelbeheer",
    pricing: "Tarieven",
    refunds: "Waarborg terugbetaling",
    documents: "Documenten",
    contract: "Documenten · Huurovereenkomsten",
    guestdocs: "Documenten · Gasteninfofiches",
    reviews: "Reviews",
    videos: "Media",
    sync: "Kalender-sync",
    activity: "Activiteitenlog",
    account: "Account",
  };

  const jumpToBookings = (statusFilter: typeof filter, pay: typeof payFilter = "all") => {
    setFilter(statusFilter);
    setPayFilter(pay);
    setTab("bookings");
  };

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AdminSidebar
          tab={tab}
          setTab={setTab}
          isHeadAdmin={isHeadAdmin}
          pendingCount={counts.pending}
          onSignOut={handleSignOut}
        />
        <SidebarInset>
          <header className="sticky top-0 z-10 flex h-14 items-center gap-3 border-b border-border/70 bg-background/80 px-4 backdrop-blur sm:px-6">
            <SidebarTrigger className="-ml-1" />
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <h1 className="truncate font-display text-base font-semibold sm:text-lg">
                {tabTitles[tab]}
              </h1>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <button
                onClick={() => setBlocking(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted sm:text-sm"
              >
                <Plus className="h-3.5 w-3.5" /> Datums blokkeren
              </button>
            </div>
          </header>

          <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
            <ConflictBanner />
            {tab === "dashboard" ? (
              <DashboardOverview
                bookings={bookings}
                loading={listQ.isLoading}
                onJump={jumpToBookings}
                onOpenQuote={(b) => setQuoting(b)}
                onOpenDetails={(b) => setDetailing(b)}
              />
            ) : tab === "customers" ? (
              <CustomersPanel bookings={bookings} onOpenGuestInfo={(b) => setGuestInfoFor(b)} />
            ) : tab === "stats" ? (
              <StatsPanel />
            ) : tab === "notifications" ? (
              <NotificationsPanel />
            ) : tab === "keykeepers" ? (
              <KeykeepersPanel bookings={bookings} />
            ) : tab === "pricing" ? (
              <PricingPanel />
            ) : tab === "refunds" ? (
              <RefundPanel bookings={bookings} />
            ) : tab === "documents" ? (
              <DocumentsPanel onOpen={setTab} />
            ) : tab === "contract" ? (
              <ContractPanel />
            ) : tab === "guestdocs" ? (
              <GuestDocsPanel bookings={bookings} onOpenGuestInfo={(b) => setGuestInfoFor(b)} />
            ) : tab === "reviews" ? (
              <ReviewsPanel />
            ) : tab === "sync" ? (
              <IcalSyncPanel />
            ) : tab === "blocks" ? (
              <BlocksPanel blocks={otherBlocks} loading={listQ.isLoading} />
            ) : tab === "videos" && isHeadAdmin ? (
              <VideosPanel />
            ) : tab === "activity" && isHeadAdmin ? (
              <ActivityLogPanel />
            ) : tab === "account" && isHeadAdmin ? (
              <AccountPanel />
            ) : (
              <BookingsTab
                bookings={bookings}
                filtered={filtered}
                counts={counts}
                payCounts={payCounts}
                filter={filter}
                setFilter={setFilter}
                payFilter={payFilter}
                setPayFilter={setPayFilter}
                search={search}
                setSearch={setSearch}
                loading={listQ.isLoading}
                onEdit={(b) => setEditing(b)}
                onQuote={(b) => setQuoting(b)}
                onDetails={(b) => setDetailing(b)}
                onGuestInfo={(b) => setGuestInfoFor(b)}
              />
            )}
          </main>
        </SidebarInset>
      </div>

      {editing && <EditDialog booking={editing} onClose={() => setEditing(null)} />}
      {quoting && <QuoteDialog booking={quoting} onClose={() => setQuoting(null)} />}
      {detailing && <PaymentDetailsDialog booking={detailing} onClose={() => setDetailing(null)} />}
      {guestInfoFor && <GuestInfoDialog booking={guestInfoFor} onClose={() => setGuestInfoFor(null)} />}
      {blocking && <BlockDialog onClose={() => setBlocking(false)} />}
    </SidebarProvider>
  );
}

/* ============================================================
   Sidebar
   ============================================================ */

type AdminTab =
  | "dashboard"
  | "bookings"
  | "blocks"
  | "customers"
  | "stats"
  | "notifications"
  | "keykeepers"
  | "pricing"
  | "refunds"
  | "documents"
  | "contract"
  | "guestdocs"
  | "reviews"
  | "videos"
  | "sync"
  | "activity"
  | "account";

function AdminSidebar({
  tab,
  setTab,
  isHeadAdmin,
  pendingCount,
  onSignOut,
}: {
  tab: AdminTab;
  setTab: (t: AdminTab) => void;
  isHeadAdmin: boolean;
  pendingCount: number;
  onSignOut: () => void;
}) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const mainItems: { key: AdminTab; label: string; icon: typeof LayoutDashboard; badge?: number }[] = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "bookings", label: "Boekingen", icon: CalendarRange, badge: pendingCount },
    { key: "customers", label: "Huurders", icon: Users },
    { key: "stats", label: "Statistieken", icon: BarChart3 },
  ];
  const opsItems: { key: AdminTab; label: string; icon: typeof LayoutDashboard }[] = [
    { key: "notifications", label: "Meldingen", icon: Bell },
    { key: "keykeepers", label: "Sleutelbeheer", icon: KeyRound },
    { key: "blocks", label: "Blokkades", icon: Ban },
    { key: "pricing", label: "Tarieven", icon: Tags },
    { key: "sync", label: "Kalender-sync", icon: CalendarSync },
  ];
  const docItems: { key: AdminTab; label: string; icon: typeof LayoutDashboard }[] = [
    { key: "guestdocs", label: "Gasteninfofiches", icon: ClipboardList },
    { key: "contract", label: "Huurovereenkomsten", icon: FileText },
  ];
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-semibold">
            CMP
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">Casa Mi Pajobe</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Admin</div>
            </div>
          )}
        </div>
      </SidebarHeader>
      <SidebarSeparator />
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Overzicht</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    isActive={tab === item.key}
                    onClick={() => setTab(item.key)}
                    tooltip={item.label}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.badge && item.badge > 0 ? (
                      <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-[10px]">
                        {item.badge}
                      </Badge>
                    ) : null}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Klantenbeheer</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {docItems.map((d) => (
                <SidebarMenuItem key={d.key}>
                  <SidebarMenuButton
                    isActive={tab === d.key}
                    onClick={() => setTab(d.key)}
                    tooltip={d.label}
                  >
                    <d.icon className="h-4 w-4" />
                    <span>{d.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={tab === "refunds"}
                  onClick={() => setTab("refunds")}
                  tooltip="Waarborg"
                >
                  <Banknote className="h-4 w-4" />
                  <span>Waarborg</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={tab === "reviews"}
                  onClick={() => setTab("reviews")}
                  tooltip="Reviews"
                >
                  <Star className="h-4 w-4" />
                  <span>Reviews</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Beheer</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {opsItems.map((item) => (
                <SidebarMenuItem key={item.key}>
                  <SidebarMenuButton
                    isActive={tab === item.key}
                    onClick={() => setTab(item.key)}
                    tooltip={item.label}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Systeem</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {isHeadAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={tab === "videos"}
                    onClick={() => setTab("videos")}
                    tooltip="Media"
                  >
                    <Film className="h-4 w-4" />
                    <span>Media</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isHeadAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={tab === "activity"}
                    onClick={() => setTab("activity")}
                    tooltip="Activiteitenlog"
                  >
                    <History className="h-4 w-4" />
                    <span>Activiteitenlog</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              {isHeadAdmin && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={tab === "account"}
                    onClick={() => setTab("account")}
                    tooltip="Account"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    <span>Account</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSignOut} tooltip="Afmelden">
              <LogOut className="h-4 w-4" />
              <span>Afmelden</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

/* ============================================================
   Dashboard overview
   ============================================================ */

function daysFromToday(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + "T00:00:00");
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function DashboardOverview({
  bookings,
  loading,
  onJump,
  onOpenQuote,
  onOpenDetails,
}: {
  bookings: AdminBooking[];
  loading: boolean;
  onJump: (
    status: "all" | "pending" | "confirmed" | "cancelled" | "flagged" | "unpaid",
    pay?: "all" | PaymentState,
  ) => void;
  onOpenQuote: (b: AdminBooking) => void;
  onOpenDetails: (b: AdminBooking) => void;
}) {
  const inquiries = bookings.filter((b) => b.kind === "inquiry");
  const pending = inquiries.filter((b) => b.status === "pending");
  const awaitingDeposit = inquiries.filter(
    (b) => b.approved_at && !b.deposit_paid_at && b.status !== "cancelled",
  );
  const awaitingBalance = inquiries.filter(
    (b) => b.deposit_paid_at && !b.balance_paid_at && b.status !== "cancelled",
  );
  const upcoming = inquiries
    .filter((b) => {
      const d = daysFromToday(b.check_in);
      return d >= 0 && d <= 14 && b.status === "confirmed";
    })
    .sort((a, b) => a.check_in.localeCompare(b.check_in));
  const upcoming7 = upcoming.filter((b) => daysFromToday(b.check_in) <= 7).length;

  const needsAction = [
    ...pending,
    ...awaitingDeposit.filter((b) => !pending.includes(b)),
  ].slice(0, 6);

  if (loading) {
    return <p className="text-sm text-muted-foreground">Dashboard laden…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={Hourglass}
          label="Openstaande aanvragen"
          value={pending.length}
          tone="amber"
          onClick={() => onJump("pending")}
        />
        <KpiCard
          icon={CircleDashed}
          label="Wacht op voorschot"
          value={awaitingDeposit.length}
          tone="blue"
          onClick={() => onJump("all", "link_sent")}
        />
        <KpiCard
          icon={Wallet}
          label="Wacht op saldo"
          value={awaitingBalance.length}
          tone="violet"
          onClick={() => onJump("confirmed")}
        />
        <KpiCard
          icon={PlaneLanding}
          label="Check-ins komende 7d"
          value={upcoming7}
          tone="green"
          onClick={() => onJump("confirmed")}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <AlertCircle className="h-4 w-4 text-amber-500" /> Actie nodig
            </CardTitle>
            <button
              onClick={() => onJump("pending")}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              Alles bekijken
            </button>
          </CardHeader>
          <CardContent className="pt-0">
            {needsAction.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Niets openstaand — top.
              </p>
            ) : (
              <ul className="divide-y divide-border/60">
                {needsAction.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-3 py-2.5">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{b.name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        <span className="font-mono">{b.reference}</span> · {b.check_in} → {b.check_out}
                      </div>
                    </div>
                    <button
                      onClick={() => (b.approved_at ? onOpenDetails(b) : onOpenQuote(b))}
                      className="shrink-0 rounded-full border border-primary/40 bg-primary/5 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/10"
                    >
                      {b.approved_at ? "Betaling" : "Offerte"}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <PlaneLanding className="h-4 w-4 text-emerald-500" /> Komende check-ins
            </CardTitle>
            <button
              onClick={() => onJump("confirmed")}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              View all
            </button>
          </CardHeader>
          <CardContent className="pt-0">
            {upcoming.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Geen check-ins de komende 14 dagen.
              </p>
            ) : (
              <ul className="divide-y divide-border/60">
                {upcoming.slice(0, 6).map((b) => {
                  const d = daysFromToday(b.check_in);
                  return (
                    <li key={b.id} className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{b.name}</div>
                        <div className="truncate text-xs text-muted-foreground">
                          {b.check_in} → {b.check_out}
                        </div>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {d === 0 ? "vandaag" : d === 1 ? "morgen" : `over ${d}d`}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  tone,
  onClick,
}: {
  icon: typeof LayoutDashboard;
  label: string;
  value: number;
  tone: "amber" | "blue" | "violet" | "green";
  onClick?: () => void;
}) {
  const toneClasses: Record<typeof tone, string> = {
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
    green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  };
  return (
    <button
      onClick={onClick}
      className="group flex items-center gap-3 rounded-xl border border-border/70 bg-card p-4 text-left shadow-sm transition hover:border-border hover:shadow"
    >
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${toneClasses[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-2xl font-semibold leading-none">{value}</div>
        <div className="mt-1 truncate text-xs text-muted-foreground">{label}</div>
      </div>
    </button>
  );
}

/* ============================================================
   Bookings tab (filters + table)
   ============================================================ */

function BlocksPanel({ blocks, loading }: { blocks: AdminBooking[]; loading: boolean }) {
  const qc = useQueryClient();
  const remove = useServerFn(deleteBooking);
  const del = useMutation({
    mutationFn: remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      qc.invalidateQueries({ queryKey: ["booked-ranges"] });
    },
  });

  const sorted = [...blocks].sort((a, b) => a.check_in.localeCompare(b.check_in));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Geblokkeerde datums om een andere reden dan een boeking (bv. onderhoud,
        eigenaarsverblijf). Gebruik "Datums blokkeren" bovenaan om een nieuwe
        blokkade toe te voegen.
      </p>
      <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
        {loading ? (
          <p className="p-8 text-sm text-muted-foreground">Blokkades laden…</p>
        ) : sorted.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground">Geen actieve blokkades.</p>
        ) : (
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Reden</th>
                <th className="px-4 py-3">Datums</th>
                <th className="px-4 py-3">Notities</th>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {sorted.map((b) => (
                <tr key={b.id}>
                  <td className="px-4 py-3 font-medium">{b.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {b.check_in} → {b.check_out}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {b.internal_notes || "—"}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{b.reference}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Blokkade ${b.reference} verwijderen?`)) {
                          del.mutate({ data: { id: b.id } });
                        }
                      }}
                      disabled={del.isPending}
                      className="rounded-full border border-border px-3 py-1 text-xs hover:bg-muted disabled:opacity-60"
                    >
                      Verwijderen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BookingsTab({
  bookings,
  filtered,
  counts,
  payCounts,
  filter,
  setFilter,
  payFilter,
  setPayFilter,
  search,
  setSearch,
  loading,
  onEdit,
  onQuote,
  onDetails,
  onGuestInfo,
}: {
  bookings: AdminBooking[];
  filtered: AdminBooking[];
  counts: {
    pending: number;
    confirmed: number;
    cancelled: number;
    flagged: number;
    unpaid: number;
  };
  payCounts: Record<PaymentState, number>;
  filter: "all" | "pending" | "confirmed" | "cancelled" | "flagged" | "unpaid";
  setFilter: (f: "all" | "pending" | "confirmed" | "cancelled" | "flagged" | "unpaid") => void;
  payFilter: "all" | PaymentState;
  setPayFilter: (p: "all" | PaymentState) => void;
  search: string;
  setSearch: (s: string) => void;
  loading: boolean;
  onEdit: (b: AdminBooking) => void;
  onQuote: (b: AdminBooking) => void;
  onDetails: (b: AdminBooking) => void;
  onGuestInfo: (b: AdminBooking) => void;
}) {
  const statusTabs = [
    ["all", `Alle`, bookings.length],
    ["pending", `Openstaand`, counts.pending],
    ["confirmed", `Bevestigd`, counts.confirmed],
    ["cancelled", `Geannuleerd`, counts.cancelled],
    ["unpaid", `Niet betaald`, counts.unpaid],
    ["flagged", `Gemarkeerd`, counts.flagged],
  ] as const;

  const history = useMemo(() => buildGuestHistory(bookings), [bookings]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="-mx-2 flex flex-1 items-center gap-1 overflow-x-auto px-2 sm:mx-0 sm:px-0">
          <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm whitespace-nowrap">
            {statusTabs.map(([k, label, count]) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                className={`rounded-full px-3.5 py-1.5 transition ${
                  filter === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                }`}
              >
                {label}{" "}
                <span className={filter === k ? "opacity-80" : "text-muted-foreground"}>
                  {count}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek ref, naam, e-mail"
            className="h-9 w-56 rounded-full border border-border bg-card pl-8 pr-3 text-sm outline-none focus:border-foreground/30"
          />
        </div>
        <select
          value={payFilter}
          onChange={(e) => setPayFilter(e.target.value as "all" | PaymentState)}
          className="h-9 rounded-full border border-border bg-card px-3 text-sm outline-none"
        >
          <option value="all">Betaling: alle</option>
          <option value="not_requested">Niet aangevraagd ({payCounts.not_requested})</option>
          <option value="link_sent">Link verzonden ({payCounts.link_sent})</option>
          <option value="checkout_started">Checkout gestart ({payCounts.checkout_started})</option>
          <option value="received">Ontvangen ({payCounts.received})</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
        {loading ? (
          <p className="p-8 text-sm text-muted-foreground">Boekingen laden…</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground">Geen boekingen passen bij dit filter.</p>
        ) : (
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Ref</th>
                <th className="px-4 py-3">Gast</th>
                <th className="px-4 py-3">Datums</th>
                <th className="px-4 py-3 whitespace-nowrap">Ontvangen</th>
                <th className="px-4 py-3">Pers.</th>
                <th className="px-4 py-3">Offerte</th>
                <th className="px-4 py-3">Betaling</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((b) => (
                <Row
                  key={b.id}
                  booking={b}
                  history={history.get(b.id)}
                  onEdit={() => onEdit(b)}
                  onQuote={() => onQuote(b)}
                  onDetails={() => onDetails(b)}
                  onGuestInfo={() => onGuestInfo(b)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Row({
  booking,
  history,
  onEdit,
  onQuote,
  onDetails,
  onGuestInfo,
}: {
  booking: AdminBooking;
  history?: GuestHistoryEntry;
  onEdit: () => void;
  onQuote: () => void;
  onDetails: () => void;
  onGuestInfo: () => void;
}) {
  const qc = useQueryClient();
  const update = useServerFn(updateBooking);
  const remove = useServerFn(deleteBooking);

  const mut = useMutation({
    mutationFn: update,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      qc.invalidateQueries({ queryKey: ["booked-ranges"] });
    },
  });
  const del = useMutation({
    mutationFn: remove,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
      qc.invalidateQueries({ queryKey: ["booked-ranges"] });
    },
  });

  const statusColor =
    booking.status === "confirmed"
      ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200"
      : booking.status === "cancelled"
      ? "bg-muted text-muted-foreground line-through"
      : "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200";

  return (
    <>
    <tr className={booking.flagged ? "bg-amber-50/40 dark:bg-amber-950/10" : undefined}>
      <td className="px-4 py-3 font-mono text-xs">
        {booking.reference}
        {booking.kind === "block" && <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px]">BLOCK</span>}
      </td>
      <td className="px-4 py-3">
        <div className="font-medium">{booking.name}</div>
        {history && history.previous.length > 0 && (
          <div
            className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
            title={`Eerdere verblijven:\n${history.previous
              .map((p) => `${p.reference}: ${p.check_in} → ${p.check_out}`)
              .join("\n")}`}
          >
            ★ Terugkerende gast · {history.previous.length}x eerder
          </div>
        )}
        {booking.kind === "inquiry" && (
          <div className="text-xs text-muted-foreground">{booking.email} · {booking.country}</div>
        )}
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {booking.check_in} → {booking.check_out}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
        {booking.created_at
          ? new Date(booking.created_at).toLocaleString("nl-BE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "—"}
      </td>
      <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
        {booking.adults}V {booking.children}K {booking.babies}B
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {booking.kind === "inquiry" && booking.total_cents ? (
          <span className="font-medium">{fmtMoney(booking.total_cents, booking.currency || "eur")}</span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <PaymentCell booking={booking} />
      </td>
      <td className="px-4 py-3">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor}`}>{statusNL(booking.status)}</span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5 text-xs">
          {booking.kind === "inquiry" && (
            <button
              onClick={onQuote}
              className="rounded-md border border-primary/40 bg-primary/5 px-2.5 py-1 font-medium text-primary hover:bg-primary/10"
            >
              {booking.approved_at ? "Heroffreren" : "Goedkeuren"}
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="rounded-md border border-border p-1 hover:bg-muted"
                aria-label="Meer acties"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs">Boeking</DropdownMenuLabel>
              {booking.kind === "inquiry" && booking.approved_at && (
                <DropdownMenuItem onClick={onDetails}>Betaaldetails</DropdownMenuItem>
              )}
              {booking.kind === "inquiry" && (
                <DropdownMenuItem onClick={onGuestInfo}>Gastinfo</DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={onEdit}>Bewerken</DropdownMenuItem>
              <DropdownMenuSeparator />
              {booking.status !== "confirmed" && (
                <DropdownMenuItem
                  onClick={() => mut.mutate({ data: { id: booking.id, status: "confirmed" } })}
                >
                  Markeer als bevestigd
                </DropdownMenuItem>
              )}
              {booking.status !== "cancelled" && (
                <DropdownMenuItem
                  onClick={() => mut.mutate({ data: { id: booking.id, status: "cancelled" } })}
                >
                  Boeking annuleren
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => mut.mutate({ data: { id: booking.id, flagged: !booking.flagged } })}
              >
                {booking.flagged ? "Demarkeren" : "Markeren"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => {
                  if (confirm(`${booking.reference} verwijderen?`))
                    del.mutate({ data: { id: booking.id } });
                }}
              >
                Verwijderen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {mut.data && !mut.data.ok && (
          <p className="mt-1 text-right text-xs text-destructive">{mut.data.message}</p>
        )}
      </td>
    </tr>
    <tr className={booking.flagged ? "bg-amber-50/40 dark:bg-amber-950/10" : undefined}>
      <td colSpan={7} className="px-4 pb-3 pt-0 text-xs text-muted-foreground">
        <div className="flex flex-wrap gap-x-5 gap-y-1">
          <span><span className="font-medium text-foreground">Slaapkamers:</span> {booking.bedrooms}</span>
          <span><span className="font-medium text-foreground">Volwassenen:</span> {booking.adults}</span>
          <span><span className="font-medium text-foreground">Kinderen:</span> {booking.children}</span>
          <span><span className="font-medium text-foreground">Baby's:</span> {booking.babies}</span>
          {booking.kind === "inquiry" && booking.phone && (
            <span><span className="font-medium text-foreground">Telefoon:</span> {booking.phone}</span>
          )}
          {booking.kind === "inquiry" && booking.country && (
            <span><span className="font-medium text-foreground">Land:</span> {booking.country}</span>
          )}
        </div>
        {booking.kind === "inquiry" && booking.message && (
          <p className="mt-1 whitespace-pre-wrap"><span className="font-medium text-foreground">Bericht:</span> {booking.message}</p>
        )}
      </td>
    </tr>
    </>
  );
}

function EditDialog({ booking, onClose }: { booking: AdminBooking; onClose: () => void }) {
  const qc = useQueryClient();
  const update = useServerFn(updateBooking);
  const mut = useMutation({
    mutationFn: update,
    onSuccess: (res) => {
      if (res.ok) {
        qc.invalidateQueries({ queryKey: ["admin-bookings"] });
        qc.invalidateQueries({ queryKey: ["booked-ranges"] });
        onClose();
      }
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = Object.fromEntries(fd.entries()) as Record<string, string>;
    mut.mutate({
      data: {
        id: booking.id,
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        country: payload.country,
        check_in: payload.check_in,
        check_out: payload.check_out,
        bedrooms: Number(payload.bedrooms),
        adults: Number(payload.adults),
        children: Number(payload.children),
        babies: Number(payload.babies),
        message: payload.message ?? "",
        internal_notes: payload.internal_notes ?? "",
        preferred_language: (payload.preferred_language as "nl" | "fr" | "en") || undefined,
      },
    });
  }

  return (
    <DialogShell title={`Bewerken ${booking.reference}`} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Input name="name" label="Naam" defaultValue={booking.name} />
          <Input name="email" label="E-mail" type="email" defaultValue={booking.email} />
          <Input name="phone" label="Telefoon" defaultValue={booking.phone} />
          <Input name="country" label="Land" defaultValue={booking.country} />
          <Input name="check_in" label="Check-in" type="date" defaultValue={booking.check_in} />
          <Input name="check_out" label="Check-out" type="date" defaultValue={booking.check_out} />
          <Input name="bedrooms" label="Slaapkamers" type="number" defaultValue={booking.bedrooms} />
          <Input name="adults" label="Volwassenen" type="number" defaultValue={booking.adults} />
          <Input name="children" label="Kinderen" type="number" defaultValue={booking.children} />
          <Input name="babies" label="Baby's" type="number" defaultValue={booking.babies} />
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Voorkeurstaal</span>
            <select
              name="preferred_language"
              defaultValue={booking.preferred_language ?? ""}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Auto (op basis van land)</option>
              <option value="nl">Nederlands</option>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Bericht van gast</span>
          <textarea name="message" rows={2} defaultValue={booking.message}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Interne notities (privé)</span>
          <textarea name="internal_notes" rows={3} defaultValue={booking.internal_notes}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </label>

        {mut.data && !mut.data.ok && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
            {mut.data.message}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm">Annuleren</button>
          <button type="submit" disabled={mut.isPending}
            className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground disabled:opacity-60">
            {mut.isPending ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

/* ============================================================
   Activity log (head admin)
   ============================================================ */

function formatAuditAction(action: string): string {
  const map: Record<string, string> = {
    "admin.sign_in": "Admin aangemeld",
    "admin.sign_in_denied": "Aanmelden geweigerd",
    "booking.approved": "Boeking goedgekeurd",
    "booking.updated": "Boeking bijgewerkt",
    "booking.deleted": "Boeking verwijderd",
    "booking.flagged": "Boeking gemarkeerd",
    "booking.cancelled": "Boeking geannuleerd",
    "booking.block_created": "Datums geblokkeerd",
    "booking.quote_sent": "Offerte verstuurd",
    "payment.link_sent": "Betaallink verstuurd",
    "payment.deposit_received": "Aanbetaling ontvangen",
    "payment.balance_received": "Saldo ontvangen",
    "keykeeper.upserted": "Sleutelbeheerder bijgewerkt",
    "keykeeper.deleted": "Sleutelbeheerder verwijderd",
    "keykeeper.booking_sent": "Boekingsinfo naar sleutelbeheerder",
    "pricing.updated": "Tarieven bijgewerkt",
    "customer.upserted": "Huurder bijgewerkt",
    "customer.deleted": "Huurder verwijderd",
  };
  return map[action] ?? action;
}

function ActivityLogPanel() {
  const fetchLogs = useServerFn(listAuditLogs);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const q = useQuery({
    queryKey: ["audit-logs", debounced],
    queryFn: () => fetchLogs({ data: { limit: 300, search: debounced } }),
  });

  const rows: AuditLogRow[] = q.data ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-base">Activiteitenlog</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Wie heeft wat gewijzigd — laatste 300 gebeurtenissen.
            </p>
          </div>
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op actie, e-mail, referentie…"
              className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="p-8 text-center text-sm text-muted-foreground">Laden…</div>
          ) : q.error ? (
            <div className="p-8 text-center text-sm text-destructive">
              {(q.error as Error).message}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nog geen activiteit gevonden.
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background/95 backdrop-blur">
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Wanneer</th>
                    <th className="px-3 py-2 font-medium">Wie</th>
                    <th className="px-3 py-2 font-medium">Actie</th>
                    <th className="px-3 py-2 font-medium">Onderwerp</th>
                    <th className="px-3 py-2 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-border/60 align-top hover:bg-muted/40">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                        {new Date(r.created_at).toLocaleString("nl-BE", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="px-3 py-2">
                        <div className="truncate">{r.actor_email ?? "—"}</div>
                        {r.ip && (
                          <div className="text-[10px] text-muted-foreground">{r.ip}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary" className="font-normal">
                          {formatAuditAction(r.action)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.entity ? (
                          <>
                            <div>{r.entity}</div>
                            {r.entity_id && (
                              <div className="truncate font-mono text-[10px] text-muted-foreground">
                                {r.entity_id}
                              </div>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {r.metadata ? (
                          <details>
                            <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                              tonen
                            </summary>
                            <pre className="mt-1 max-w-md overflow-auto whitespace-pre-wrap break-all rounded bg-muted/60 p-2 text-[10px] leading-snug">
                              {(() => {
                                try {
                                  return JSON.stringify(JSON.parse(r.metadata), null, 2);
                                } catch {
                                  return r.metadata;
                                }
                              })()}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ManualCustomerDialog({
  initial,
  onClose,
  onSaved,
}: {
  initial: ManualCustomer | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const save = useServerFn(upsertManualCustomer);
  const [name, setName] = useState(initial?.name ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [country, setCountry] = useState(initial?.country ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: save,
    onSuccess: () => onSaved(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : "Opslaan mislukt"),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!name.trim()) {
      setErr("Naam is verplicht");
      return;
    }
    mut.mutate({
      data: {
        id: initial?.id,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        country: country.trim(),
        notes: notes.trim(),
      },
    });
  }

  return (
    <DialogShell title={initial ? `Huurder bewerken · ${initial.name}` : "Huurder toevoegen"} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Naam *</span><input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            required
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">E-mail</span><input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
          <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Telefoon</span><input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
            />
          </label>
        </div>
        <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Land</span><input
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <label className="block"><span className="mb-1 block text-xs font-medium text-muted-foreground">Notities</span><textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        {err && <p className="text-xs text-destructive">{err}</p>}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
          >
            Annuleren
          </button>
          <button
            type="submit"
            disabled={mut.isPending}
            className="rounded-full bg-primary px-5 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

/* ============================================================
 * Customers panel — derived from bookings, grouped by email
 * ============================================================ */

type Customer = {
  key: string; // normalized email or fallback id
  manualId: string | null;
  notes: string | null;
  internalRating: number | null;
  internalReview: string | null;
  email: string;
  name: string;
  phone: string;
  country: string;
  bookings: AdminBooking[];
  firstSeen: string;
  lastSeen: string;
  confirmedCount: number;
  pendingCount: number;
  cancelledCount: number;
  totalPaidCents: number;
  currency: string;
};

function buildCustomers(bookings: AdminBooking[], manual: ManualCustomer[]): Customer[] {
  const map = new Map<string, Customer>();
  for (const b of bookings) {
    if (b.kind !== "inquiry") continue;
    const normEmail = (b.email || "").trim().toLowerCase();
    const key = normEmail || `id:${b.id}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        key,
        manualId: null,
        notes: null,
        internalRating: null,
        internalReview: null,
        email: b.email || "",
        name: b.name || "",
        phone: b.phone || "",
        country: b.country || "",
        bookings: [b],
        firstSeen: b.created_at,
        lastSeen: b.created_at,
        confirmedCount: b.status === "confirmed" ? 1 : 0,
        pendingCount: b.status === "pending" ? 1 : 0,
        cancelledCount: b.status === "cancelled" ? 1 : 0,
        totalPaidCents:
          (b.deposit_paid_at ? b.deposit_cents ?? 0 : 0) +
          (b.balance_paid_at ? b.balance_cents ?? 0 : 0),
        currency: b.currency || "eur",
      });
    } else {
      existing.bookings.push(b);
      if (b.created_at < existing.firstSeen) existing.firstSeen = b.created_at;
      if (b.created_at > existing.lastSeen) existing.lastSeen = b.created_at;
      if (b.status === "confirmed") existing.confirmedCount++;
      else if (b.status === "pending") existing.pendingCount++;
      else if (b.status === "cancelled") existing.cancelledCount++;
      existing.totalPaidCents +=
        (b.deposit_paid_at ? b.deposit_cents ?? 0 : 0) +
        (b.balance_paid_at ? b.balance_cents ?? 0 : 0);
      // Prefer name/phone from most recent booking
      if (b.created_at >= existing.lastSeen) {
        if (b.name) existing.name = b.name;
        if (b.phone) existing.phone = b.phone;
        if (b.country) existing.country = b.country;
      }
    }
  }

  // Merge in manually added customers.
  for (const m of manual) {
    const normEmail = (m.email || "").trim().toLowerCase();
    const key = normEmail || `manual:${m.id}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        key,
        manualId: m.id,
        notes: m.notes,
        internalRating: m.internal_rating,
        internalReview: m.internal_review,
        email: m.email ?? "",
        name: m.name,
        phone: m.phone ?? "",
        country: m.country ?? "",
        bookings: [],
        firstSeen: m.created_at,
        lastSeen: m.created_at,
        confirmedCount: 0,
        pendingCount: 0,
        cancelledCount: 0,
        totalPaidCents: 0,
        currency: "eur",
      });
    } else {
      existing.manualId = m.id;
      existing.notes = m.notes;
      existing.internalRating = m.internal_rating;
      existing.internalReview = m.internal_review;
      // Fill in missing booking-derived contact details with manual fields.
      if (!existing.name) existing.name = m.name;
      if (!existing.phone && m.phone) existing.phone = m.phone;
      if (!existing.country && m.country) existing.country = m.country;
      if (!existing.email && m.email) existing.email = m.email;
    }
  }

  const list = Array.from(map.values());
  list.sort((a, b) => (a.lastSeen < b.lastSeen ? 1 : -1));
  for (const c of list) {
    c.bookings.sort((a, b) => (a.check_in < b.check_in ? 1 : -1));
  }
  return list;
}

function CustomersPanel({
  bookings,
  onOpenGuestInfo,
}: {
  bookings: AdminBooking[];
  onOpenGuestInfo: (b: AdminBooking) => void;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<Customer | null>(null);
  const [editing, setEditing] = useState<ManualCustomer | "new" | null>(null);

  const qc = useQueryClient();
  const fetchManual = useServerFn(listManualCustomers);
  const removeManual = useServerFn(deleteManualCustomer);
  const manualQ = useQuery({
    queryKey: ["manual-customers"],
    queryFn: () => fetchManual(),
  });
  const del = useMutation({
    mutationFn: removeManual,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manual-customers"] }),
  });

  const customers = buildCustomers(bookings, manualQ.data ?? []);
  const q = search.trim().toLowerCase();
  const filtered = q
    ? customers.filter((c) => {
        if (
          c.name.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.phone.toLowerCase().includes(q) ||
          c.country.toLowerCase().includes(q) ||
          (c.notes ?? "").toLowerCase().includes(q)
        )
          return true;
        return c.bookings.some((b) => b.reference.toLowerCase().includes(q));
      })
    : customers;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Huurdersdatabase</h2>
          <p className="text-xs text-muted-foreground">
            {customers.length} huurder{customers.length === 1 ? "" : "s"} · uit boekingen + handmatig toegevoegd
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek op naam, e-mail, telefoon, land, referentie…"
            className="w-72 max-w-full rounded-full border border-border bg-card px-4 py-2 text-sm outline-none focus:border-primary"
          />
          <button
            onClick={() => setEditing("new")}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            + Huurder toevoegen
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
        {filtered.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground">
            {customers.length === 0 ? "Nog geen huurders." : "Geen huurders gevonden."}
          </p>
        ) : (
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Naam</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Land</th>
                <th className="px-4 py-3">Intern</th>
                <th className="px-4 py-3 text-right">Boekingen</th>
                <th className="px-4 py-3 whitespace-nowrap">Laatste activiteit</th>
                <th className="px-4 py-3 text-right">Betaald</th>
                <th className="px-4 py-3 text-right">Dossier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((c) => (
                <tr key={c.key}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{c.name || "—"}</span>
                      {c.manualId && c.bookings.length === 0 && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          handmatig
                        </span>
                      )}
                    </div>
                    {c.confirmedCount > 0 && (
                      <div className="mt-0.5 text-[10px] uppercase tracking-wider text-green-700 dark:text-green-300">
                        Terugkerend · {c.confirmedCount} bevestigd
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>{c.email || "—"}</div>
                    <div className="text-muted-foreground">{c.phone || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{c.country || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.internalRating ? (
                      <span
                        className="whitespace-nowrap text-amber-600 dark:text-amber-400"
                        title={c.internalReview ?? undefined}
                      >
                        {"★".repeat(c.internalRating)}
                        <span className="text-muted-foreground">{"★".repeat(5 - c.internalRating)}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-xs">
                    <span className="font-medium">{c.bookings.length}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      ({c.confirmedCount}C · {c.pendingCount}P · {c.cancelledCount}X)
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(c.lastSeen).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right text-xs font-medium">
                    {c.totalPaidCents > 0 ? fmtMoney(c.totalPaidCents, c.currency) : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-1.5">
                      <button
                        onClick={() => setOpen(c)}
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                      >
                        Openen
                      </button>
                      {c.manualId && (
                        <>
                          <button
                            onClick={() => {
                              const m = (manualQ.data ?? []).find((x) => x.id === c.manualId);
                              if (m) setEditing(m);
                            }}
                            className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                          >
                            Bewerken
                          </button>
                          {c.bookings.length === 0 && (
                            <button
                              disabled={del.isPending}
                              onClick={() => {
                                if (confirm(`${c.name} verwijderen?`)) del.mutate({ data: { id: c.manualId! } });
                              }}
                              className="rounded border border-destructive/40 px-2 py-1 text-xs text-destructive hover:bg-destructive/10"
                            >
                              Wis
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <CustomerDossierDialog
          customer={open}
          onClose={() => setOpen(null)}
          onOpenGuestInfo={(b) => {
            setOpen(null);
            onOpenGuestInfo(b);
          }}
        />
      )}

      {editing && (
        <ManualCustomerDialog
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["manual-customers"] });
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function InternalRatingSection({ customer }: { customer: Customer }) {
  const qc = useQueryClient();
  const save = useServerFn(rateCustomer);
  const [rating, setRating] = useState<number | null>(customer.internalRating);
  const [review, setReview] = useState(customer.internalReview ?? "");
  const [saved, setSaved] = useState(false);

  const mut = useMutation({
    mutationFn: save,
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      qc.invalidateQueries({ queryKey: ["manual-customers"] });
    },
  });

  return (
    <section className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Interne beoordeling <span className="normal-case tracking-normal">(enkel voor ons zichtbaar)</span>
      </h3>
      <div className="mt-2 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setRating(rating === n ? null : n)}
            className="p-0.5"
            aria-label={`${n} ster${n === 1 ? "" : "ren"}`}
          >
            <Star
              className={`h-5 w-5 ${
                rating !== null && n <= rating
                  ? "fill-amber-500 text-amber-500"
                  : "text-muted-foreground"
              }`}
            />
          </button>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">
          {rating ? `${rating}/5` : "Nog geen score"}
        </span>
      </div>
      <textarea
        value={review}
        onChange={(e) => setReview(e.target.value)}
        rows={3}
        placeholder="Interne notitie: willen we deze gast terug? Bv. netjes achtergelaten, laat vertrokken…"
        className="mt-3 w-full rounded-lg border border-border bg-card p-2 text-sm outline-none focus:border-primary"
      />
      <div className="mt-2 flex items-center gap-3">
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() =>
            mut.mutate({
              data: {
                customerId: customer.manualId ?? undefined,
                name: customer.name || undefined,
                email: customer.email || undefined,
                rating,
                review,
              },
            })
          }
          className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {mut.isPending ? "Opslaan…" : "Beoordeling opslaan"}
        </button>
        {saved && <span className="text-xs text-green-600 dark:text-green-400">Opgeslagen</span>}
        {mut.isError && <span className="text-xs text-destructive">Opslaan mislukt</span>}
      </div>
    </section>
  );
}

function CustomerDossierDialog({
  customer,
  onClose,
  onOpenGuestInfo,
}: {
  customer: Customer;
  onClose: () => void;
  onOpenGuestInfo: (b: AdminBooking) => void;
}) {
  return (
    <DialogShell title={`Dossier · ${customer.name || customer.email || "Huurder"}`} onClose={onClose}>
      <div className="space-y-5">
        <section className="rounded-xl border border-border/60 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profiel</h3>
          <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <div className="text-muted-foreground">Naam</div>
            <div>{customer.name || "—"}</div>
            <div className="text-muted-foreground">E-mail</div>
            <div>{customer.email || "—"}</div>
            <div className="text-muted-foreground">Telefoon</div>
            <div>{customer.phone || "—"}</div>
            <div className="text-muted-foreground">Land</div>
            <div>{customer.country || "—"}</div>
            <div className="text-muted-foreground">Huurder sinds</div>
            <div>{new Date(customer.firstSeen).toLocaleDateString()}</div>
            <div className="text-muted-foreground">Laatste activiteit</div>
            <div>{new Date(customer.lastSeen).toLocaleDateString()}</div>
            <div className="text-muted-foreground">Totaal betaald</div>
            <div>
              {customer.totalPaidCents > 0
                ? fmtMoney(customer.totalPaidCents, customer.currency)
                : "—"}
            </div>
          </div>
          {customer.notes ? (
            <div className="mt-3 rounded-lg bg-muted/40 p-3 text-sm">
              <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Notities</div>
              <p className="whitespace-pre-wrap">{customer.notes}</p>
            </div>
          ) : null}
        </section>

        <InternalRatingSection customer={customer} />

        <RefundDossierSection bookings={customer.bookings} />

        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Boekingen ({customer.bookings.length})
          </h3>
          <div className="mt-2 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[560px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">Ref</th>
                  <th className="px-3 py-2">Datums</th>
                  <th className="px-3 py-2">Pers.</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Offerte</th>
                  <th className="px-3 py-2 text-right">Gastinfo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {customer.bookings.map((b) => (
                  <tr key={b.id}>
                    <td className="px-3 py-2 font-mono text-xs">{b.reference}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">
                      {b.check_in} → {b.check_out}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                      {b.adults}V {b.children}K {b.babies}B
                    </td>
                    <td className="px-3 py-2 text-xs">{statusNL(b.status)}</td>
                    <td className="px-3 py-2 text-right text-xs">
                      {b.total_cents ? fmtMoney(b.total_cents, b.currency || "eur") : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onOpenGuestInfo(b)}
                        className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
                      >
                        Bekijken
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <div className="mt-5 flex justify-end border-t border-border pt-4">
        <button onClick={onClose} className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground">
          Sluiten
        </button>
      </div>
    </DialogShell>
  );
}

/* ---------- pricing panel ---------- */

type RateDraft = {
  period: string;
  beds2: string;
  beds3: string;
  beds4: string;
  water2: string;
  water3: string;
  water4: string;
};

function toDraft(rows: PricingSettings["rates"]): RateDraft[] {
  return rows.map((r) => ({
    period: r.period,
    beds2: r.beds2 == null ? "" : String(r.beds2),
    beds3: r.beds3 == null ? "" : String(r.beds3),
    beds4: r.beds4 == null ? "" : String(r.beds4),
    water2: r.water2 == null ? "" : String(r.water2),
    water3: r.water3 == null ? "" : String(r.water3),
    water4: r.water4 == null ? "" : String(r.water4),
  }));
}

function fromDraft(rows: RateDraft[]): PricingSettings["rates"] {
  return rows.map((r) => ({
    period: r.period.trim(),
    beds2: r.beds2.trim() === "" ? null : Number(r.beds2),
    beds3: r.beds3.trim() === "" ? null : Number(r.beds3),
    beds4: r.beds4.trim() === "" ? null : Number(r.beds4),
    water2: r.water2.trim() === "" ? null : Number(r.water2),
    water3: r.water3.trim() === "" ? null : Number(r.water3),
    water4: r.water4.trim() === "" ? null : Number(r.water4),
  }));
}

function PricingPanel() {
  const qc = useQueryClient();
  const getFn = useServerFn(getPricingSettings);
  const saveFn = useServerFn(updatePricingSettings);

  const settingsQ = useQuery({
    queryKey: ["pricing-settings"],
    queryFn: () => getFn(),
  });

  const [rows, setRows] = useState<RateDraft[]>([]);
  const [lmEnabled, setLmEnabled] = useState(false);
  const [lmPercent, setLmPercent] = useState("0");
  const [lmDays, setLmDays] = useState("30");
  const [lmEndDate, setLmEndDate] = useState<string>("");
  const [lmEn, setLmEn] = useState("Last-minute deal");
  const [lmNl, setLmNl] = useState("Last-minute deal");
  const [lmFr, setLmFr] = useState("Offre de dernière minute");
  const [lmDe, setLmDe] = useState("Last-Minute-Angebot");
  const [lmTxtEn, setLmTxtEn] = useState("");
  const [lmTxtNl, setLmTxtNl] = useState("");
  const [lmTxtFr, setLmTxtFr] = useState("");
  const [lmTxtDe, setLmTxtDe] = useState("");
  const [waterPrice, setWaterPrice] = useState("4");
  const [overrides, setOverrides] = useState<
    Array<{ start: string; end: string; label: string; enabled: boolean }>
  >([]);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!settingsQ.data) return;
    setRows(toDraft(settingsQ.data.rates));
    setLmEnabled(settingsQ.data.lastMinuteEnabled);
    setLmPercent(String(settingsQ.data.lastMinutePercent));
    setLmDays(String(settingsQ.data.lastMinuteWithinDays));
    setLmEndDate(settingsQ.data.lastMinuteEndDate ?? "");
    setLmEn(settingsQ.data.lastMinuteLabel.en);
    setLmNl(settingsQ.data.lastMinuteLabel.nl);
    setLmFr(settingsQ.data.lastMinuteLabel.fr);
    setLmDe(settingsQ.data.lastMinuteLabel.de);
    setLmTxtEn(settingsQ.data.lastMinuteText?.en ?? "");
    setLmTxtNl(settingsQ.data.lastMinuteText?.nl ?? "");
    setLmTxtFr(settingsQ.data.lastMinuteText?.fr ?? "");
    setLmTxtDe(settingsQ.data.lastMinuteText?.de ?? "");
    setWaterPrice(String(settingsQ.data.waterPricePerM3 ?? 4));
    setOverrides(
      (settingsQ.data.leadTimeOverrides ?? []).map((o) => ({
        start: o.start,
        end: o.end,
        label: o.label ?? "",
        enabled: o.enabled !== false,
      })),
    );
  }, [settingsQ.data]);

  const mut = useMutation({
    mutationFn: (data: PricingSettings) => saveFn({ data }),
    onSuccess: () => {
      setSavedAt(Date.now());
      setErr(null);
      qc.invalidateQueries({ queryKey: ["pricing-settings"] });
      toast.success("Opgeslagen", {
        description: "Je uitzonderingen en tarieven zijn bijgewerkt.",
      });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Opslaan mislukt";
      setErr(msg);
      toast.error("Opslaan mislukt", { description: msg });
    },
  });

  function updateRow(i: number, patch: Partial<RateDraft>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function addRow() {
    setRows((prev) => [...prev, { period: "01.01.2027 - 31.01.2027", beds2: "", beds3: "", beds4: "", water2: "", water3: "", water4: "" }]);
  }
  function removeRow(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  function save() {
    setErr(null);
    try {
      const payload: PricingSettings = {
        rates: fromDraft(rows),
        lastMinuteEnabled: lmEnabled,
        lastMinutePercent: Number(lmPercent) || 0,
        lastMinuteWithinDays: Number(lmDays) || 0,
        lastMinuteEndDate: lmEndDate ? lmEndDate : null,
        lastMinuteLabel: { en: lmEn, nl: lmNl, fr: lmFr, de: lmDe },
        lastMinuteText: { en: lmTxtEn, nl: lmTxtNl, fr: lmTxtFr, de: lmTxtDe },
        waterPricePerM3: Number(waterPrice) || 0,
        leadTimeOverrides: overrides
          .filter((o) => o.start && o.end)
          .map((o) => ({
            start: o.start,
            end: o.end,
            enabled: o.enabled,
            ...(o.label.trim() ? { label: o.label.trim() } : {}),
          })),
      };
      mut.mutate(payload);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Ongeldige invoer");
    }
  }

  if (settingsQ.isLoading) return <p className="p-8 text-sm text-muted-foreground">Laden…</p>;

  return (
    <section className="space-y-10">
      <div>
        <h2 className="text-lg font-semibold">Weektarieven</h2>
        <p className="text-sm text-muted-foreground">
          Periode-formaat: <code className="rounded bg-muted px-1">dd.mm.yyyy - dd.mm.yyyy</code>. Laat een prijs leeg voor "niet beschikbaar".
        </p>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-3">Periode</th>
                <th className="px-3 py-3 w-28">2 slpk (€/wk)</th>
                <th className="px-3 py-3 w-28">3 slpk (€/wk)</th>
                <th className="px-3 py-3 w-28">4 slpk (€/wk)</th>
                <th className="px-3 py-3 w-24">Water 2 (m³/wk)</th>
                <th className="px-3 py-3 w-24">Water 3 (m³/wk)</th>
                <th className="px-3 py-3 w-24">Water 4 (m³/wk)</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t border-border/60">
                  <td className="px-3 py-2">
                    <input
                      value={r.period}
                      onChange={(e) => updateRow(i, { period: e.target.value })}
                      className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
                      placeholder="01.06.2026 - 10.07.2026"
                    />
                  </td>
                  {(["beds2", "beds3", "beds4", "water2", "water3", "water4"] as const).map((k) => (
                    <td key={k} className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        step={k.startsWith("water") ? 0.1 : 1}
                        value={r[k]}
                        onChange={(e) => updateRow(i, { [k]: e.target.value } as Partial<RateDraft>)}
                        className="w-full rounded border border-input bg-background px-2 py-1.5 text-right tabular-nums text-sm outline-none focus:border-primary"
                        placeholder="—"
                      />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeRow(i)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                      aria-label="Rij verwijderen"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Nog geen tariefperiodes.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="mt-3 rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
        >
          + Periode toevoegen
        </button>

        <div className="mt-6 max-w-xs">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Waterprijs meerverbruik (€/m³)</span>
            <input
              type="number"
              min={0}
              step={0.1}
              value={waterPrice}
              onChange={(e) => setWaterPrice(e.target.value)}
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
            <span className="mt-1 block text-[11px] text-muted-foreground">
              Wordt aangerekend op het contract per m³ boven de inbegrepen hoeveelheid.
            </span>
          </label>
        </div>
      </div>

      <div className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Datums vrijgeven (uitzonderingen)</h2>
            <p className="text-sm text-muted-foreground">
              Binnen deze reeksen mogen gasten boeken ook al is het minder dan
              5 dagen op voorhand, én mag in juli/augustus op eender welke dag
              worden ingecheckt (geen zaterdag-tot-zaterdag regel).
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              setOverrides((prev) => [...prev, { start: "", end: "", label: "", enabled: true }])
            }
            className="rounded-full border border-border px-4 py-1.5 text-sm hover:bg-muted"
          >
            + Periode toevoegen
          </button>
        </div>

        {overrides.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground">
            Nog geen uitzonderingen. De standaardregels gelden.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {overrides.map((o, i) => (
              <div
                key={i}
                className={`grid gap-3 sm:grid-cols-[auto_1fr_1fr_1.5fr_auto] sm:items-end ${o.enabled ? "" : "opacity-60"}`}
              >
                <label className="flex items-center gap-2 pb-2 text-xs font-medium text-muted-foreground sm:pb-3">
                  <input
                    type="checkbox"
                    checked={o.enabled}
                    onChange={(e) =>
                      setOverrides((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, enabled: e.target.checked } : x)),
                      )
                    }
                    className="size-4"
                  />
                  Actief
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">Van (check-in vanaf)</span>
                  <input
                    type="date"
                    value={o.start}
                    onChange={(e) =>
                      setOverrides((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, start: e.target.value } : x)),
                      )
                    }
                    className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">Tot (check-in tot en met dag vóór)</span>
                  <input
                    type="date"
                    value={o.end}
                    onChange={(e) =>
                      setOverrides((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, end: e.target.value } : x)),
                      )
                    }
                    className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-muted-foreground">Notitie (optioneel)</span>
                  <input
                    type="text"
                    value={o.label}
                    onChange={(e) =>
                      setOverrides((prev) =>
                        prev.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)),
                      )
                    }
                    placeholder="bv. Last-minute augustus"
                    className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                  />
                </label>
                <button
                  type="button"
                  onClick={() =>
                    setOverrides((prev) => prev.filter((_, idx) => idx !== i))
                  }
                  className="rounded-full border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  Verwijderen
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={mut.isPending}
            className="rounded-full bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {mut.isPending ? "Opslaan…" : "Uitzonderingen opslaan"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold">Last-minute deal</h2>
            <p className="text-sm text-muted-foreground">
              Korting wordt automatisch toegepast als de check-in binnen de drempel valt.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={lmEnabled}
              onChange={(e) => setLmEnabled(e.target.checked)}
              className="size-4"
            />
            Ingeschakeld
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Korting (%)</span>
            <input
              type="number"
              min={0}
              max={90}
              value={lmPercent}
              onChange={(e) => setLmPercent(e.target.value)}
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">Toepassen wanneer check-in binnen (dagen) ligt</span>
            <input
              type="number"
              min={0}
              max={365}
              value={lmDays}
              onChange={(e) => setLmDays(e.target.value)}
              className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
            />
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-muted-foreground">
              Einddatum (optioneel — overschrijft het dagenvenster)
            </span>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="date"
                value={lmEndDate}
                onChange={(e) => setLmEndDate(e.target.value)}
                className="w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
              {lmEndDate && (
                <button
                  type="button"
                  onClick={() => setLmEndDate("")}
                  className="rounded-full border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-muted"
                >
                  Wissen
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Indien ingesteld verbergt de deal automatisch na deze datum en wordt het dagenvenster genegeerd.
            </p>
          </label>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {([
            ["EN", lmEn, setLmEn],
            ["NL", lmNl, setLmNl],
            ["FR", lmFr, setLmFr],
            ["DE", lmDe, setLmDe],
          ] as const).map(([code, val, setter]) => (
            <label key={code} className="block">
              <span className="text-xs font-medium text-muted-foreground">Label ({code})</span>
              <input
                value={val}
                onChange={(e) => setter(e.target.value)}
                className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
              />
            </label>
          ))}
        </div>

        <div className="mt-6">
          <h3 className="text-sm font-semibold">Banner-tekst (bovenaan de website)</h3>
          <p className="text-xs text-muted-foreground">
            Laat leeg om de standaardtekst te gebruiken. Je kan <code className="rounded bg-muted px-1">{"{percent}"}</code> gebruiken; die wordt vervangen door het kortingspercentage.
          </p>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            {([
              ["NL", lmTxtNl, setLmTxtNl],
              ["EN", lmTxtEn, setLmTxtEn],
              ["FR", lmTxtFr, setLmTxtFr],
              ["DE", lmTxtDe, setLmTxtDe],
            ] as const).map(([code, val, setter]) => (
              <label key={code} className="block">
                <span className="text-xs font-medium text-muted-foreground">Tekst ({code})</span>
                <textarea
                  value={val}
                  rows={2}
                  maxLength={240}
                  onChange={(e) => setter(e.target.value)}
                  placeholder="bv. Last-minute: {percent}% korting bij check-out vóór 11 juli"
                  className="mt-1 w-full rounded border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                />
              </label>
            ))}
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {err}
        </div>
      )}
      {savedAt && !err && !mut.isPending && (
        <div className="rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-700 dark:text-green-300">
          Opgeslagen.
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={mut.isPending}
          className="rounded-full bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          {mut.isPending ? "Opslaan…" : "Tarieven opslaan"}
        </button>
      </div>
    </section>
  );
}

function BlockDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const block = useServerFn(createBlock);
  const [reason, setReason] = useState<"booking" | "other" | "on_request">("booking");
  const [otherLabel, setOtherLabel] = useState("");
  const mut = useMutation({
    mutationFn: block,
    onSuccess: (res) => {
      if (res.ok) {
        qc.invalidateQueries({ queryKey: ["admin-bookings"] });
        qc.invalidateQueries({ queryKey: ["booked-ranges"] });
        onClose();
      }
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const p = Object.fromEntries(fd.entries()) as Record<string, string>;
    const label =
      reason === "booking"
        ? "Boeking (extern)"
        : reason === "on_request"
        ? (otherLabel.trim() || "Op aanvraag")
        : (otherLabel.trim() || "Geblokkeerd");
    mut.mutate({
      data: {
        check_in: p.check_in,
        check_out: p.check_out,
        label,
        notes: p.notes ?? "",
        on_request: reason === "on_request",
      },
    });
  }

  return (
    <DialogShell title="Datums blokkeren" onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <span className="text-xs font-medium text-muted-foreground">Reden</span>
          <div className="flex flex-wrap gap-2">
            <label className={`flex-1 min-w-[140px] cursor-pointer rounded-md border px-3 py-2 text-sm ${reason === "booking" ? "border-primary bg-primary/10" : "border-input"}`}>
              <input
                type="radio"
                name="reason"
                value="booking"
                checked={reason === "booking"}
                onChange={() => setReason("booking")}
                className="mr-2"
              />
              Voor een boeking
            </label>
            <label className={`flex-1 min-w-[140px] cursor-pointer rounded-md border px-3 py-2 text-sm ${reason === "other" ? "border-primary bg-primary/10" : "border-input"}`}>
              <input
                type="radio"
                name="reason"
                value="other"
                checked={reason === "other"}
                onChange={() => setReason("other")}
                className="mr-2"
              />
              Andere reden
            </label>
            <label className={`flex-1 min-w-[140px] cursor-pointer rounded-md border px-3 py-2 text-sm ${reason === "on_request" ? "border-primary bg-primary/10" : "border-input"}`}>
              <input
                type="radio"
                name="reason"
                value="on_request"
                checked={reason === "on_request"}
                onChange={() => setReason("on_request")}
                className="mr-2"
              />
              Op aanvraag
            </label>
          </div>
          {reason === "on_request" && (
            <p className="text-xs text-muted-foreground">
              Deze datums blijven selecteerbaar op de website en worden getoond als
              &quot;op aanvraag&quot; in de kalenderlegende.
            </p>
          )}
          {(reason === "other" || reason === "on_request") && (
            <input
              type="text"
              value={otherLabel}
              onChange={(e) => setOtherLabel(e.target.value)}
              placeholder="Bijv. Onderhoud, eigenaarsverblijf…"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input name="check_in" label="Check-in" type="date" required />
          <Input name="check_out" label="Check-out" type="date" required />
        </div>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Notities</span>
          <textarea name="notes" rows={2} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        </label>
        {mut.data && !mut.data.ok && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
            {mut.data.message}
          </p>
        )}
        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <button type="button" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm">Cancel</button>
          <button type="submit" disabled={mut.isPending}
            className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground disabled:opacity-60">
            {mut.isPending ? "Opslaan…" : "Blok aanmaken"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function DialogShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4" onClick={onClose}>
      <div className="my-2 max-h-[96vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-card p-4 shadow-xl sm:my-0 sm:max-h-[90vh] sm:p-8"
        onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, name, type = "text", defaultValue, required, value, onChange }:
  { label: string; name: string; type?: string; defaultValue?: string | number; required?: boolean;
    value?: string; onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <input name={name} type={type} required={required}
        {...(value !== undefined ? { value, onChange } : { defaultValue })}
        className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
    </label>
  );
}

/* ---------- payment cell ---------- */

function fmtMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function PaymentCell({ booking }: { booking: AdminBooking }) {
  if (booking.kind !== "inquiry") {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  if (!booking.approved_at || !booking.total_cents) {
    return <span className="text-xs text-muted-foreground">Geen offerte</span>;
  }
  const cur = booking.currency || "eur";
  return (
    <div className="space-y-0.5 text-xs">
      <div className="font-medium">{fmtMoney(booking.total_cents, cur)} totaal</div>
      <PaymentLine
        label="Deposit"
        amount={booking.deposit_cents ?? 0}
        currency={cur}
        paidAt={booking.deposit_paid_at}
        token={booking.deposit_payment_token}
        sessionId={booking.stripe_deposit_session_id}
      />
      <PaymentLine
        label="Balance"
        amount={booking.balance_cents ?? 0}
        currency={cur}
        paidAt={booking.balance_paid_at}
        token={booking.balance_payment_token}
        sessionId={booking.stripe_balance_session_id}
        dueDate={booking.balance_due_date}
      />
    </div>
  );
}

function PaymentLine({
  label, amount, currency, paidAt, token, sessionId, dueDate,
}: {
  label: string;
  amount: number;
  currency: string;
  paidAt: string | null;
  token: string | null;
  sessionId: string | null;
  dueDate?: string | null;
}) {
  if (paidAt) {
    const when = new Date(paidAt).toLocaleDateString();
    return (
      <div className="text-green-700 dark:text-green-300">
        <span className="font-medium">{label}:</span> ✓ ontvangen {when}
      </div>
    );
  }
  let badge: { text: string; cls: string };
  if (sessionId) {
    badge = { text: "checkout gestart", cls: "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200" };
  } else if (token) {
    badge = { text: "link verzonden", cls: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200" };
  } else {
    badge = { text: "niet aangevraagd", cls: "bg-muted text-muted-foreground" };
  }
  return (
    <div className="text-muted-foreground">
      <span className="font-medium text-foreground">{label}:</span>{" "}
      {fmtMoney(amount, currency)}
      {dueDate ? ` · verschuldigd ${dueDate}` : ""}{" "}
      <span className={`ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.text}</span>
    </div>
  );
}

/* ---------- quote dialog ---------- */

function QuotedOnInquiry({ booking }: { booking: AdminBooking }) {
  const b = booking.quoted_breakdown;
  if (
    !booking.quoted_total_cents &&
    !booking.quoted_rental_cents &&
    !b
  ) {
    return null;
  }
  const cur = booking.quoted_currency || booking.currency || "eur";
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
        Prijs getoond aan gast bij aanvraag
      </p>
      <dl className="mt-2 grid grid-cols-2 gap-y-1 text-sm">
        {booking.quoted_rental_cents != null && (
          <>
            <dt className="text-muted-foreground">Basishuur</dt>
            <dd className="text-right">{fmtMoney(booking.quoted_rental_cents, cur)}</dd>
          </>
        )}
        {b?.cleaning != null && (
          <>
            <dt className="text-muted-foreground">Schoonmaak</dt>
            <dd className="text-right">{fmtMoney((b.cleaning ?? 0) * 100, cur)}</dd>
          </>
        )}
        {b?.linenTotal != null && b.linenTotal > 0 && (
          <>
            <dt className="text-muted-foreground">Handdoeken</dt>
            <dd className="text-right">{fmtMoney((b.linenTotal ?? 0) * 100, cur)}</dd>
          </>
        )}
        {b?.babyPackTotal != null && b.babyPackTotal > 0 && (
          <>
            <dt className="text-muted-foreground">Babypack</dt>
            <dd className="text-right">{fmtMoney((b.babyPackTotal ?? 0) * 100, cur)}</dd>
          </>
        )}
        {booking.quoted_discount_cents != null && booking.quoted_discount_cents > 0 && (
          <>
            <dt className="text-muted-foreground">
              Korting{b?.discount?.label ? ` (${b.discount.label})` : ""}
            </dt>
            <dd className="text-right">
              −{fmtMoney(booking.quoted_discount_cents, cur)}
              {booking.quoted_rental_cents
                ? ` (${Math.round((booking.quoted_discount_cents / booking.quoted_rental_cents) * 1000) / 10}%)`
                : ""}
            </dd>
          </>
        )}
        {booking.quoted_total_cents != null && (
          <>
            <dt className="font-medium">Getoond totaal</dt>
            <dd className="text-right font-semibold">
              {fmtMoney(booking.quoted_total_cents, cur)}
            </dd>
          </>
        )}
      </dl>
      {b?.segments && b.segments.length > 0 && (
        <details className="mt-3 text-xs text-muted-foreground">
          <summary className="cursor-pointer">Tariefperiodes</summary>
          <ul className="mt-1 space-y-0.5">
            {b.segments.map((s, i) => (
              <li key={i}>
                {s.label} · {s.nights}n
                {s.weeklyRate != null ? ` · €${s.weeklyRate}/wk` : ""}
                {s.subtotal != null ? ` = €${s.subtotal}` : ""}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function QuoteDialog({ booking, onClose }: { booking: AdminBooking; onClose: () => void }) {
  const qc = useQueryClient();
  const approve = useServerFn(approveBookingWithQuote);
  const [rentalInput, setRentalInput] = useState(
    booking.quoted_rental_cents ? (booking.quoted_rental_cents / 100).toFixed(2) : "",
  );
  const [discountEuro, setDiscountEuro] = useState(
    booking.quoted_discount_cents ? (booking.quoted_discount_cents / 100).toFixed(2) : "",
  );
  const [discountPct, setDiscountPct] = useState(() => {
    const r = booking.quoted_rental_cents ?? 0;
    const d = booking.quoted_discount_cents ?? 0;
    return r > 0 && d > 0 ? String(Math.round((d / r) * 1000) / 10) : "";
  });
  const [sendNow, setSendNow] = useState(false);
  const [totalInput, setTotalInput] = useState(
    booking.quoted_total_cents
      ? (booking.quoted_total_cents / 100).toFixed(2)
      : booking.total_cents
      ? Math.max(0, booking.total_cents / 100 - 500).toFixed(2)
      : "",
  );
  const [securityInput, setSecurityInput] = useState("500");
  const rentalNum = Number(rentalInput.replace(",", "."));
  const rentalBase = Number.isFinite(rentalNum) && rentalNum > 0 ? rentalNum : 0;
  const totalNum = Number(totalInput.replace(",", "."));
  const totalBase = Number.isFinite(totalNum) && totalNum > 0 ? totalNum : 0;
  const securityNum = Number(securityInput.replace(",", "."));
  const securityBase = Number.isFinite(securityNum) && securityNum >= 0 ? securityNum : 0;
  const discountNum = Number(discountEuro.replace(",", "."));
  const discountRaw = Number.isFinite(discountNum) && discountNum > 0 ? discountNum : 0;
  // Korting geldt uitsluitend op de netto huurprijs: nooit meer dan de basishuur.
  const discountBase = rentalBase > 0 ? Math.min(discountRaw, rentalBase) : 0;
  const discountCapped = discountRaw > discountBase;
  // Het ingevulde totaal is het voorgestelde totaal ná korting: de korting mag
  // hier niet nogmaals afgetrokken worden (anders telt ze dubbel).
  const netTotal = totalBase;
  const grandTotal = netTotal + securityBase;
  const netRental = Math.max(0, rentalBase - discountBase);
  const cleaningFee = 230;
  const towelPersons = (booking.adults ?? 0) + (booking.children ?? 0);
  const towelsFee = towelPersons * 10;
  const otherFees = netTotal - netRental - cleaningFee - towelsFee;

  function onDiscountEuro(v: string) {
    setDiscountEuro(v);
    const amount = Number(v.replace(",", "."));
    setDiscountPct(
      rentalBase > 0 && Number.isFinite(amount) && amount > 0
        ? String(Math.round((amount / rentalBase) * 1000) / 10)
        : "",
    );
  }
  function onDiscountPct(v: string) {
    setDiscountPct(v);
    const pct = Number(v.replace(",", "."));
    setDiscountEuro(
      rentalBase > 0 && Number.isFinite(pct) && pct > 0
        ? ((rentalBase * pct) / 100).toFixed(2)
        : "",
    );
  }

  const mut = useMutation({
    mutationFn: approve,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
    },
  });

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const total = Number(fd.get("total"));
    const rental = Number(fd.get("rental"));
    const percent = Number(fd.get("depositPercent"));
    const security = Number(fd.get("security"));
    const depositOverride = Number(fd.get("depositOverride"));
    const balanceOverride = Number(fd.get("balanceOverride"));
    const currency = (fd.get("currency") as "eur" | "usd" | "gbp") || "eur";
    if (!Number.isFinite(total) || total <= 0) return;
    const rentalCents = Number.isFinite(rental) && rental > 0 ? Math.round(rental * 100) : undefined;
    const discountAmount = discountBase;
    // De server trekt de korting nog één keer af van het totaal, dus sturen we
    // hier het brutototaal door (voorgesteld totaal + korting).
    const grossTotal = total + discountAmount;
    mut.mutate({
      data: {
        id: booking.id,
        totalCents: Math.round(grossTotal * 100),
        rentalCents,
        discountCents:
          Number.isFinite(discountAmount) && discountAmount > 0
            ? Math.round(discountAmount * 100)
            : undefined,
        sendDepositEmail: sendNow,
        depositPercent: Number.isFinite(percent) && percent > 0 ? percent : undefined,
        securityDepositCents:
          String(fd.get("security") ?? "").trim() !== "" && Number.isFinite(security) && security >= 0
            ? Math.round(security * 100)
            : undefined,
        depositCentsOverride:
          Number.isFinite(depositOverride) && depositOverride > 0
            ? Math.round(depositOverride * 100)
            : undefined,
        balanceCentsOverride:
          Number.isFinite(balanceOverride) && balanceOverride > 0
            ? Math.round(balanceOverride * 100)
            : undefined,
        currency,
      },
    });
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const result = mut.data;

  return (
    <DialogShell title={`Offerte voor ${booking.reference}`} onClose={onClose}>
      {!result ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <QuotedOnInquiry booking={booking} />
          <p className="text-sm text-muted-foreground">
            Geef de basishuurprijs en het totaal in (incl. schoonmaak &amp; handdoeken).
            De terugbetaalbare waarborg wordt automatisch bij het totaal geteld. Voorschot = 25% van de basishuur. Saldo = 75% van de basishuur + schoonmaak + handdoeken + waarborg, te betalen 8 weken voor check-in ({booking.check_in}).
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">
                Basishuur (excl. schoonmaak, handdoeken)
              </span>
              <input
                name="rental"
                type="number"
                value={rentalInput}
                onChange={(e) => setRentalInput(e.currentTarget.value)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </label>
            <div />
          </div>
          <div className="rounded-xl border border-border p-3">
            <p className="text-sm font-medium">Korting (enkel op de netto huurprijs)</p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Korting (€)</span>
                <input
                  type="number"
                  value={discountEuro}
                  onChange={(e) => onDiscountEuro(e.currentTarget.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-muted-foreground">Korting (%)</span>
                <input
                  type="number"
                  value={discountPct}
                  onChange={(e) => onDiscountPct(e.currentTarget.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {rentalBase > 0 && discountBase > 0
                ? `Netto huurprijs: € ${netRental.toFixed(2)} (korting ${discountPct}% = € ${discountBase.toFixed(2)}). Schoonmaak, handdoeken en waarborg blijven ongewijzigd.`
                : "Vul een bedrag óf een percentage in — het andere veld wordt automatisch berekend. De korting geldt enkel op de basishuur."}
            </p>
            {discountCapped && (
              <p className="mt-1 text-xs text-destructive">
                Korting beperkt tot de basishuur (€ {rentalBase.toFixed(2)}).
              </p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
            <Input
              name="total"
              label="Voorgesteld totaalbedrag ná korting (incl. schoonmaak & handdoeken, excl. waarborg en taksen)"
              type="number"
              value={totalInput}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTotalInput(e.currentTarget.value)}
              required
            />
            <label className="block">
              <span className="text-xs font-medium text-muted-foreground">Munteenheid</span>
              <select
                name="currency"
                defaultValue={booking.currency || "eur"}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="eur">EUR</option>
                <option value="usd">USD</option>
                <option value="gbp">GBP</option>
              </select>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">
            Lokale btw / toeristentaks wordt automatisch berekend bij de checkout door Stripe.
          </p>
          <div className="rounded-xl border border-border bg-muted/40 p-3 text-sm">
            <p className="text-sm font-medium">Uitsplitsing (controle)</p>
            <dl className="mt-2 space-y-1">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">
                  Huur (netto{discountBase > 0 ? `, na korting € ${discountBase.toFixed(2)}` : ""})
                </dt>
                <dd>€ {netRental.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Eindschoonmaak</dt>
                <dd>€ {cleaningFee.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">
                  Handdoekenpakket ({towelPersons} × € 10,00)
                </dt>
                <dd>€ {towelsFee.toFixed(2)}</dd>
              </div>
              {Math.abs(otherFees) >= 0.01 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Overig / verschil met totaal</dt>
                  <dd className={otherFees < 0 ? "text-destructive" : undefined}>
                    € {otherFees.toFixed(2)}
                  </dd>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-1">
                <dt className="text-muted-foreground">Voorgesteld totaalbedrag (excl. waarborg)</dt>
                <dd>€ {netTotal.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Terugbetaalbare waarborg</dt>
                <dd>€ {securityBase.toFixed(2)}</dd>
              </div>
              <div className="flex justify-between border-t border-border pt-1 font-semibold">
                <dt>Totaal inclusief waarborg</dt>
                <dd>€ {grandTotal.toFixed(2)}</dd>
              </div>
            </dl>
            {Math.abs(otherFees) >= 0.01 && (
              <p className="mt-2 text-xs text-muted-foreground">
                Het verschil ontstaat doordat het totaalbedrag niet exact gelijk is aan netto huur +
                eindschoonmaak (€ {cleaningFee.toFixed(2)}) + handdoekenpakket. Pas de basishuur of
                het totaal aan als dit niet klopt.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 rounded-xl border border-border p-3 text-sm">
            <input
              type="checkbox"
              checked={sendNow}
              onChange={(e) => setSendNow(e.currentTarget.checked)}
              className="mt-0.5"
            />
            <span>
              Voorschot-betaallink meteen mailen naar de gast
              <span className="block text-xs text-muted-foreground">
                Laat uit om de links enkel aan te maken — je kiest daarna zelf wanneer je ze
                verstuurt met de knop “Link mailen”.
              </span>
            </span>
          </label>
          <details className="rounded-xl border border-border p-3">
            <summary className="cursor-pointer text-sm font-medium">
              Bedragen voor de huurder aanpassen (Stripe)
            </summary>
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input name="depositPercent" label="Voorschot % van de basishuur" type="number" defaultValue="25" />
                <Input
                  name="security"
                  label="Terugbetaalbare waarborg (€)"
                  type="number"
                  value={securityInput}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSecurityInput(e.currentTarget.value)}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Input
                  name="depositOverride"
                  label="Vast voorschotbedrag (€) — optioneel"
                  type="number"

                  defaultValue={booking.deposit_cents ? (booking.deposit_cents / 100).toFixed(2) : ""}
                />
                <Input
                  name="balanceOverride"
                  label="Vast saldobedrag (€) — optioneel"
                  type="number"

                  defaultValue={booking.balance_cents ? (booking.balance_cents / 100).toFixed(2) : ""}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Laat de vaste bedragen leeg om ze automatisch te berekenen uit het percentage en de waarborg.
              </p>
            </div>
          </details>
          {mut.isError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
              {(mut.error as Error).message}
            </p>
          )}
          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <button type="button" onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm">
              Annuleren
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground disabled:opacity-60"
            >
              {mut.isPending ? "Opslaan…" : "Betaallinks aanmaken"}
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-5 text-sm">
          <div className="rounded-xl border border-green-500/40 bg-green-500/10 p-4">
            Offerte opgeslagen. Deel de voorschotlink met de gast. De saldolink kan 8 weken voor check-in worden verstuurd (verschuldigd tegen {result.balanceDueDate}).
            {result.discountCents > 0 && (
              <div className="mt-1 text-xs">
                Korting toegepast: −{fmtMoney(result.discountCents, result.currency)}
                {result.discountPercent ? ` (${result.discountPercent}%)` : ""}
              </div>
            )}
          </div>

          {result.emailStatus === "sent" && result.emailRecipient && (
            <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-4 text-blue-900 dark:text-blue-200">
              ✓ Voorschot-betaallink gemaild naar {result.emailRecipient}.
            </div>
          )}
          {result.emailStatus === "failed" && (
            <div className="rounded-xl border border-destructive/50 bg-destructive/10 p-4 text-destructive">
              Offerte opgeslagen, maar de voorschotmail kon niet worden verzonden: {result.emailError ?? "onbekende fout"}.
              Gebruik de "Link mailen"-knop hieronder om opnieuw te proberen.
            </div>
          )}
          {result.emailStatus === "skipped" && !sendNow && (
            <div className="rounded-xl border border-border bg-muted/30 p-4 text-muted-foreground">
              Nog niets gemaild — gebruik hieronder “Link mailen” wanneer je de betaallink wil versturen.
            </div>
          )}
          {result.emailStatus === "skipped" && sendNow && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-amber-900 dark:text-amber-200">
              Voorschotmail is niet verzonden (geen geldig e-mailadres of adres is onderdrukt).
            </div>
          )}

          <LinkRow
            label={`Voorschot · ${fmtMoney(result.depositCents, result.currency)} (25%)`}
            url={`${origin}/pay/${result.depositToken}`}
            bookingId={booking.id}
            kind="deposit"
            paid={!!booking.deposit_paid_at}
          />
          <LinkRow
            label={`Saldo · ${fmtMoney(result.balanceCents, result.currency)} (75%)`}
            url={`${origin}/pay/${result.balanceToken}`}
            bookingId={booking.id}
            kind="balance"
            paid={!!booking.balance_paid_at}
          />

          <div className="flex justify-end border-t border-border pt-4">
            <button onClick={onClose} className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground">
              Klaar
            </button>
          </div>
        </div>
      )}
    </DialogShell>
  );
}

function LinkRow({
  label,
  url,
  bookingId,
  kind,
  paid,
}: {
  label: string;
  url: string;
  bookingId?: string;
  kind?: "deposit" | "balance";
  paid?: boolean;
}) {
  const sendEmail = useServerFn(sendPaymentLinkEmail);
  const mut = useMutation({
    mutationFn: sendEmail,
    onSuccess: (res: { ok: boolean; recipient: string; reason: string | null }) => {
      if (res?.ok) toast.success(`Mail verzonden naar ${res.recipient ?? "de gast"}`);
      else toast.error(`Mail niet verzonden (${res?.reason ?? "onbekende reden"})`);
    },
    onError: (e: unknown) =>
      toast.error("Mail verzenden mislukt", {
        description: e instanceof Error ? e.message : undefined,
      }),
  });
  const previewEmail = useServerFn(previewPaymentLinkEmail);
  const previewMut = useMutation({
    mutationFn: previewEmail,
    onSuccess: (res: { html: string; subject: string }) => {
      const w = window.open("", "_blank", "noopener,noreferrer,width=720,height=900");
      if (!w) {
        alert("Kon voorbeeld niet openen — sta pop-ups toe voor deze site.");
        return;
      }
      w.document.open();
      w.document.write(
        `<!doctype html><html><head><meta charset="utf-8"><title>${res.subject.replace(/</g, "&lt;")}</title></head><body style="margin:0;background:#f6f6f4">${res.html}</body></html>`,
      );
      w.document.close();
    },
  });
  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-xs font-medium text-muted-foreground">
        <span>{label}</span>
        {paid !== undefined && (
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
              paid
                ? "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200"
                : "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200"
            }`}
          >
            {paid ? "BETAALD" : "NIET BETAALD"}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
        />
        <button
          type="button"
          onClick={() => navigator.clipboard.writeText(url)}
          className="rounded-md border border-border px-3 py-2 text-xs hover:bg-muted"
        >
          Kopiëren
        </button>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="rounded-md border border-border px-3 py-2 text-xs hover:bg-muted"
        >
          Openen
        </a>
        {bookingId && kind && (
          <button
            type="button"
            disabled={previewMut.isPending}
            onClick={() => previewMut.mutate({ data: { bookingId, kind } })}
            className="rounded-md border border-border px-3 py-2 text-xs hover:bg-muted disabled:opacity-60"
            title="Bekijk de mail met echte boekingsdata"
          >
            {previewMut.isPending ? "Laden…" : "Voorbeeld"}
          </button>
        )}
        {bookingId && kind && (
          <button
            type="button"
            disabled={mut.isPending}
            onClick={() => mut.mutate({ data: { bookingId, kind } })}
            className="rounded-md border border-border bg-primary px-3 py-2 text-xs text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {mut.isPending ? "Verzenden…" : mut.isSuccess ? "Verzonden ✓" : "Link mailen"}
          </button>
        )}
      </div>
      {previewMut.isError && (
        <p className="mt-1 text-xs text-destructive">{(previewMut.error as Error).message}</p>
      )}
      {mut.isError && (
        <p className="mt-1 text-xs text-destructive">{(mut.error as Error).message}</p>
      )}
      {mut.isSuccess && mut.data?.ok && (
        <p className="mt-1 text-xs text-muted-foreground">Gemaild naar {mut.data.recipient}.</p>
      )}
      {mut.isSuccess && !mut.data?.ok && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
          Niet verzonden ({mut.data?.reason ?? "overgeslagen"}).
        </p>
      )}
    </div>
  );
}

/* ---------- payment details dialog ---------- */

function PaymentDetailsDialog({ booking, onClose }: { booking: AdminBooking; onClose: () => void }) {
  const fetchEvents = useServerFn(listPaymentEvents);
  const eventsQ = useQuery({
    queryKey: ["payment-events", booking.id],
    queryFn: () => fetchEvents({ data: { bookingId: booking.id } }),
  });

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const cur = booking.currency || "eur";
  const depositUrl = booking.deposit_payment_token ? `${origin}/pay/${booking.deposit_payment_token}` : null;
  const balanceUrl = booking.balance_payment_token ? `${origin}/pay/${booking.balance_payment_token}` : null;

  return (
    <DialogShell
      title={`Boeking ${booking.reference} · ${booking.name || "Onbekend"} · ${booking.check_in} → ${booking.check_out}`}
      onClose={onClose}
    >
      <div className="space-y-6 text-sm">
        <section className="rounded-xl border border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-primary">
              {booking.reference}
            </span>
            <span className="font-medium text-foreground">{booking.name || "—"}</span>
            {booking.email ? <span>· {booking.email}</span> : null}
            <span>·</span>
            <span>{booking.check_in} → {booking.check_out}</span>
            <span>·</span>
            <span>{booking.adults}V {booking.children}K {booking.babies}B · {booking.bedrooms} slk</span>
            {booking.created_at ? (
              <>
                <span>·</span>
                <span>Ontvangen {new Date(booking.created_at).toLocaleString("nl-BE")}</span>
              </>
            ) : null}
          </div>
        </section>
        <section className="rounded-xl border border-border/60 bg-muted/30 p-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opsplitsing</h3>
          <dl className="mt-2 grid grid-cols-2 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">Totaal</dt>
            <dd className="text-right font-medium">{booking.total_cents ? fmtMoney(booking.total_cents, cur) : "—"}</dd>
            <dt className="text-muted-foreground">Voorschot (25%)</dt>
            <dd className="text-right">{booking.deposit_cents ? fmtMoney(booking.deposit_cents, cur) : "—"}</dd>
            <dt className="text-muted-foreground">Saldo (75%)</dt>
            <dd className="text-right">{booking.balance_cents ? fmtMoney(booking.balance_cents, cur) : "—"}</dd>
            <dt className="text-muted-foreground">Saldo verschuldigd</dt>
            <dd className="text-right">{booking.balance_due_date ?? "—"}</dd>
            <dt className="text-muted-foreground">Betaalstatus</dt>
            <dd className="text-right font-medium">
              {booking.deposit_paid_at && booking.balance_paid_at ? (
                <span className="text-green-700 dark:text-green-300">✓ Volledig betaald</span>
              ) : booking.deposit_paid_at ? (
                <span className="text-amber-700 dark:text-amber-300">Voorschot betaald · saldo open</span>
              ) : (
                <span className="text-red-700 dark:text-red-300">✗ Nog niets betaald</span>
              )}
            </dd>
          </dl>
        </section>

        <section className="space-y-4">
          <PaymentSection
            label="Voorschot"
            amount={booking.deposit_cents}
            currency={cur}
            paidAt={booking.deposit_paid_at}
            url={depositUrl}
            sessionId={booking.stripe_deposit_session_id}
            bookingId={booking.id}
            kind="deposit"
          />
          <DepositEmailStatus booking={booking} />
          <PaymentSection
            label="Saldo"
            amount={booking.balance_cents}
            currency={cur}
            paidAt={booking.balance_paid_at}
            url={balanceUrl}
            sessionId={booking.stripe_balance_session_id}
            bookingId={booking.id}
            kind="balance"
          />
        </section>

        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Recente webhook-events
          </h3>
          {eventsQ.isLoading ? (
            <p className="mt-2 text-xs text-muted-foreground">Laden…</p>
          ) : !eventsQ.data || eventsQ.data.length === 0 ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Nog geen Stripe-events ontvangen voor deze boeking.
            </p>
          ) : (
            <ul className="mt-2 divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
              {eventsQ.data.map((ev) => <EventRow key={ev.id} ev={ev} />)}
            </ul>
          )}
        </section>

        <BookingEmailsSection booking={booking} />

        <div className="flex justify-end border-t border-border pt-4">
          <button onClick={onClose} className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground">
            Sluiten
          </button>
        </div>
      </div>
    </DialogShell>
  );
}

function GuestInfoSection({ bookingId }: { bookingId: string }) {
  return <GuestInfoSectionImpl bookingId={bookingId} />;
}

function DepositEmailStatus({ booking }: { booking: AdminBooking }) {
  const sentAt = booking.deposit_email_sent_at;
  const status = booking.deposit_email_status;
  const messageId = booking.deposit_email_message_id;
  const lang = booking.preferred_language;
  const qc = useQueryClient();
  const resend = useServerFn(resendDepositReceivedEmail);
  const [feedback, setFeedback] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: resend,
    onSuccess: (res) => {
      const msg = res.ok
        ? "Bevestigingsmail opnieuw verzonden."
        : res.message ?? `Niet verzonden (${res.status}).`;
      setFeedback(msg);
      if (res.ok) toast.success("Mail verzonden", { description: msg });
      else toast.error("Mail niet verzonden", { description: msg });
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : "Verzenden mislukt";
      setFeedback(msg);
      toast.error("Mail verzenden mislukt", { description: msg });
    },
  });

  const canResend =
    !!booking.deposit_paid_at &&
    !!booking.email &&
    (status === "failed" ||
      status === "suppressed" ||
      status === "dlq" ||
      (!!sentAt && status !== "sent"));

  const tone =
    status === "sent"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : status === "suppressed"
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : status
          ? "bg-rose-50 text-rose-800 border-rose-200"
          : "bg-muted/40 text-muted-foreground border-border/60";

  const label =
    status === "sent"
      ? "Verzonden"
      : status === "suppressed"
        ? "Onderdrukt (afmelding)"
        : status === "failed"
          ? "Mislukt"
          : status
            ? status
            : booking.deposit_paid_at
              ? "Nog niet verzonden"
              : "Wacht op betaling voorschot";

  return (
    <div className={`rounded-xl border p-3 text-xs ${tone}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold uppercase tracking-wider">
          Bevestigingsmail voorschot
        </span>
        <span className="font-medium">{label}</span>
      </div>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1">
        <dt className="opacity-70">Verzonden op</dt>
        <dd>{sentAt ? new Date(sentAt).toLocaleString() : "—"}</dd>
        <dt className="opacity-70">Mail-ID</dt>
        <dd className="break-all font-mono text-[11px]">{messageId ?? "—"}</dd>
        <dt className="opacity-70">Taal</dt>
        <dd className="uppercase">{lang ?? "auto"}</dd>
      </dl>
      {canResend ? (
        <div className="mt-3 flex items-center justify-end gap-2">
          {feedback ? <span className="opacity-80">{feedback}</span> : null}
          <button
            type="button"
            onClick={() => {
              setFeedback(null);
              mut.mutate({ data: { bookingId: booking.id } });
            }}
            disabled={mut.isPending}
            className="rounded-full border border-current/30 bg-white/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider hover:bg-white disabled:opacity-60"
          >
            {mut.isPending ? "Verzenden…" : "Opnieuw verzenden"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function GuestInfoDialog({ booking, onClose }: { booking: AdminBooking; onClose: () => void }) {
  return (
    <DialogShell title={`Gastinfo · ${booking.reference}`} onClose={onClose}>
      <GuestInfoSectionImpl bookingId={booking.id} />
      <div className="mt-5 flex justify-end border-t border-border pt-4">
        <button onClick={onClose} className="rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground">
          Sluiten
        </button>
      </div>
    </DialogShell>
  );
}

/* ---------- Booking emails section ---------- */

const TEMPLATE_LABELS: Record<string, string> = {
  "deposit-received": "Bevestiging voorschot",
  "pre-arrival": "Welkom (2 dagen voor aankomst)",
  "pre-checkout": "Check-out uitleg + review",
  "balance-reminder": "Herinnering saldo",
  "booking-confirmation-guest": "Boekingsbevestiging",
  "guest-inquiry-received": "Aanvraag ontvangen",
  "payment-link": "Betaallink",
  "contract-sign-link": "Contract — ondertekenlink",
  "contract-signed": "Contract ondertekend",
  "guest-info-received": "Gastinfo ontvangen",
  "guest-info-reminder": "Gastinfofiche — herinnering",
  "welcome-book": "Welkomstboekje (14 dagen voor aankomst)",
  "keykeeper-booking-info": "Keykeeper — boekingsinfo",
  "admin-booking-notification": "Admin — nieuwe aanvraag",
  "booking-confirmation-admin": "Admin — bevestiging",
  "admin-registered": "Admin — geregistreerd",
};

function AudienceBadge({ template }: { template?: string | null }) {
  const audience = getEmailAudience(template);
  const tone =
    audience === "admin"
      ? "border-slate-700 bg-slate-800 text-white"
      : audience === "partner"
        ? "border-violet-300 bg-violet-100 text-violet-800"
        : "border-emerald-300 bg-emerald-100 text-emerald-800";
  return (
    <span
      className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
    >
      {AUDIENCE_LABEL[audience]}
    </span>
  );
}

function statusTone(status: string | null | undefined) {
  if (status === "sent")
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  if (status === "suppressed")
    return "bg-amber-50 text-amber-800 border-amber-200";
  if (status === "pending")
    return "bg-sky-50 text-sky-800 border-sky-200";
  if (!status) return "bg-muted/40 text-muted-foreground border-border/60";
  return "bg-rose-50 text-rose-800 border-rose-200";
}

function BookingEmailsSection({ booking }: { booking: AdminBooking }) {
  const fetchEmails = useServerFn(listBookingEmails);
  const resend = useServerFn(resendBookingEmail);
  const qc = useQueryClient();
  const emailsQ = useQuery({
    queryKey: ["booking-emails", booking.id],
    queryFn: () => fetchEmails({ data: { bookingId: booking.id } }),
  });
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  const [open, setOpen] = useState(false);
  const mut = useMutation({
    mutationFn: resend,
    onSuccess: (res, vars) => {
      const tpl = (vars as any).data.templateName as string;
      const msg = res.ok
        ? "Opnieuw verzonden."
        : res.message ?? `Niet verzonden (${res.status}).`;
      setFeedback((f) => ({ ...f, [tpl]: msg }));
      if (res.ok) toast.success(`Mail verzonden · ${tpl}`, { description: msg });
      else toast.error(`Mail niet verzonden · ${tpl}`, { description: msg });
      qc.invalidateQueries({ queryKey: ["booking-emails", booking.id] });
      qc.invalidateQueries({ queryKey: ["admin-bookings"] });
    },
    onError: (e: unknown, vars) => {
      const tpl = (vars as any).data.templateName as string;
      const msg = e instanceof Error ? e.message : "Verzenden mislukt";
      setFeedback((f) => ({ ...f, [tpl]: msg }));
      toast.error(`Mail verzenden mislukt · ${tpl}`, { description: msg });
    },
  });

  const rows = (emailsQ.data ?? []).filter(
    (r: BookingEmailLogRow) => getEmailAudience(r.template_name) === "guest",
  );

  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-1 py-1 text-left hover:bg-muted/40"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          E-mails naar de huurder{rows.length ? ` (${rows.length})` : ""}
        </span>
        <span className="text-xs text-muted-foreground">{open ? "▲ Minimaliseren" : "▼ Tonen"}</span>
      </button>
      {!open ? null : !booking.email ? (
        <p className="text-xs text-muted-foreground">
          Geen e-mailadres voor deze boeking.
        </p>
      ) : emailsQ.isLoading ? (
        <p className="text-xs text-muted-foreground">Laden…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Nog geen e-mails geregistreerd voor {booking.email}.
        </p>
      ) : (
        <ul className="divide-y divide-border/60 overflow-hidden rounded-xl border border-border/60">
          {rows.map((r: BookingEmailLogRow, i) => (
            <li
              key={(r.message_id ?? "") + i}
              className="grid grid-cols-[1fr,auto] gap-2 p-2.5 text-xs"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                  <AudienceBadge template={r.template_name} />
                  <span>
                    {TEMPLATE_LABELS[r.template_name ?? ""] ?? r.template_name ?? "Onbekend"}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                  {r.message_id ? (
                    <span className="ml-2 break-all font-mono opacity-70">
                      {r.message_id}
                    </span>
                  ) : null}
                </div>
                {r.error_message ? (
                  <div className="mt-1 text-[11px] text-rose-700">{r.error_message}</div>
                ) : null}
              </div>
              <div className="flex items-start justify-end">
                <span
                  className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusTone(r.status)}`}
                >
                  {r.status ?? "—"}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {open && booking.email ? (
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Opnieuw verzenden
          </div>
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {RESENDABLE_TEMPLATE_NAMES.map((tpl) => {
              const isPending = mut.isPending && mut.variables?.data.templateName === tpl;
              const msg = feedback[tpl];
              return (
                <div
                  key={tpl}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background px-2.5 py-1.5"
                >
                  <span className="truncate text-xs">{TEMPLATE_LABELS[tpl] ?? tpl}</span>
                  <div className="flex items-center gap-2">
                    {msg ? (
                      <span className="text-[10px] text-muted-foreground">{msg}</span>
                    ) : null}
                    <button
                      type="button"
                      disabled={isPending}
                      onClick={() => {
                        setFeedback((f) => ({ ...f, [tpl]: "" }));
                        mut.mutate({ data: { bookingId: booking.id, templateName: tpl } });
                      }}
                      className="rounded-full border border-border/60 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider hover:bg-muted disabled:opacity-60"
                    >
                      {isPending ? "…" : "Verstuur"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function GuestInfoSectionImpl({ bookingId }: { bookingId: string }) {
  const fetchInfo = useServerFn(getGuestInfoForBooking);
  const q = useQuery({
    queryKey: ["guest-info-admin", bookingId],
    queryFn: () => fetchInfo({ data: { bookingId } }),
  });
  const createFn = useServerFn(createGuestInfoSubmission);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  async function handleCreate() {
    setCreating(true);
    setCreateError(null);
    try {
      await createFn({ data: { bookingId } });
      await q.refetch();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Aanmaken mislukt");
    } finally {
      setCreating(false);
    }
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const sub = q.data && q.data.exists ? q.data.submission : null;
  const persons = q.data && q.data.exists ? q.data.persons : [];
  const formUrl = sub ? `${origin}/guest-info/${sub.token}` : null;

  function downloadCsv() {
    if (!sub) return;
    const esc = (v: unknown) => {
      const s = v == null ? "" : String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines: string[] = [];
    lines.push("Guest information file");
    lines.push(`Submitted,${esc(sub.submitted_at ?? "not submitted")}`);
    lines.push("");
    lines.push("Renter");
    lines.push(`Name,${esc([sub.renter_first_name, sub.renter_family_name].filter(Boolean).join(" "))}`);
    lines.push(`Address,${esc([sub.renter_address, sub.renter_postcode, sub.renter_city, sub.renter_country].filter(Boolean).join(", "))}`);
    lines.push(`Email,${esc(sub.renter_email ?? "")}`);
    lines.push(`Phone,${esc(sub.renter_phone ?? "")}`);
    lines.push(`IBAN (deposit refund),${esc((sub as any).renter_iban ?? "")}`);
    lines.push("");
    lines.push("Stay");
    lines.push(`Bedrooms requested,${esc(sub.bedrooms_requested ?? "")}`);
    lines.push(`Baby kit,${sub.baby_kit ? "yes" : "no"}`);
    lines.push(`Travel mode,${esc(sub.travel_mode ?? "")}`);
    lines.push(`Found via,${esc(sub.found_via ?? "")}`);
    if (Array.isArray(sub.travel_info) && sub.travel_info.length > 0) {
      lines.push("");
      lines.push("Travel info");
      lines.push("Date,Airport,Flight,Departure,Arrival");
      for (const t of sub.travel_info as Array<Record<string, unknown>>) {
        lines.push([t.date, t.airport, t.flight_number, t.departure_time, t.arrival_time].map(esc).join(","));
      }
    }
    lines.push("");
    lines.push("Guests");
    lines.push("#,Type,Gender,First name,Family name,Nationality,DOB,Doc type,Document number,Issued,Expires");
    for (const p of persons) {
      lines.push([
        p.position, p.is_baby ? "baby" : "guest", p.gender, p.first_name, p.family_name,
        p.nationality, p.date_of_birth, (p as any).document_type, p.document_number, p.document_issue_date, (p as any).document_expiry_date,
      ].map(esc).join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = sub.submitted_at ? new Date(sub.submitted_at).toISOString().slice(0, 10) : "draft";
    a.download = `guest-info-${bookingId.slice(0, 8)}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <section>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Gastinformatieformulier</h3>
      {q.isLoading ? (
        <p className="mt-2 text-xs text-muted-foreground">Laden…</p>
      ) : !sub ? (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-muted-foreground">Nog niet aangemaakt.</p>
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
          >
            {creating ? "Bezig…" : "Gastinfofiche aanmaken"}
          </button>
          {createError ? <p className="text-xs text-destructive">{createError}</p> : null}
        </div>
      ) : (
        <div className="mt-2 space-y-3 rounded-xl border border-border/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">
                {sub.submitted_at ? `Ingediend ${new Date(sub.submitted_at).toLocaleString()}` : "Nog niet ingediend"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {(sub as any).link_open_count && (sub as any).link_open_count > 0
                  ? `Link ${(sub as any).link_open_count}× geopend · eerste ${new Date((sub as any).link_first_opened_at).toLocaleString()} · laatste ${new Date((sub as any).link_last_opened_at).toLocaleString()}`
                  : "Link nog niet geopend"}
              </p>
            </div>
            {formUrl ? (
              <div className="flex flex-wrap items-center gap-2">
                <a
                  href={formUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
                >
                  Gastinfofiche openen
                </a>
                <button
                  type="button"
                  onClick={downloadCsv}
                  className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted"
                >
                  CSV downloaden
                </button>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(formUrl)}
                  className="rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted"
                >
                  Link kopiëren
                </button>
              </div>
            ) : null}
          </div>

          {sub.submitted_at ? (
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <div className="text-muted-foreground">Huurder</div>
              <div>{[sub.renter_first_name, sub.renter_family_name].filter(Boolean).join(" ") || "—"}</div>
              <div className="text-muted-foreground">Adres</div>
              <div>{[sub.renter_address, sub.renter_postcode, sub.renter_city, sub.renter_country].filter(Boolean).join(", ") || "—"}</div>
              <div className="text-muted-foreground">E-mail / telefoon</div>
              <div>{[sub.renter_email, sub.renter_phone].filter(Boolean).join(" · ") || "—"}</div>
              <div className="text-muted-foreground">IBAN (terugbetaling waarborg)</div>
              <div className="font-mono">{(sub as any).renter_iban || "—"}</div>
              <div className="text-muted-foreground">Slaapkamers / babypack</div>
              <div>{sub.bedrooms_requested ?? "—"} · {sub.baby_kit ? "ja" : "nee"}</div>
              <div className="text-muted-foreground">Aankomst</div>
              <div>{sub.travel_mode || "—"}</div>
              <div className="text-muted-foreground">Gevonden via</div>
              <div>{sub.found_via || "—"}</div>
            </div>
          ) : null}

          {persons.length > 0 ? (
            <div className="overflow-hidden rounded-lg border border-border/60">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-left text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5">#</th>
                    <th className="px-2 py-1.5">Naam</th>
                    <th className="px-2 py-1.5">Nat.</th>
                    <th className="px-2 py-1.5">Geb.</th>
                    <th className="px-2 py-1.5">Document</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/40">
                  {persons.map((p) => (
                    <tr key={p.id}>
                      <td className="px-2 py-1.5">{p.is_baby ? "baby" : p.position}</td>
                      <td className="px-2 py-1.5">{[p.gender, p.first_name, p.family_name].filter(Boolean).join(" ")}</td>
                      <td className="px-2 py-1.5">{p.nationality ?? "—"}</td>
                      <td className="px-2 py-1.5">{p.date_of_birth ?? "—"}</td>
                      <td className="px-2 py-1.5">
                        {(p as any).document_type === "passport" ? "Paspoort" : (p as any).document_type === "id" ? "ID" : ""}
                        {(p as any).document_type ? " · " : ""}
                        {p.document_number ?? "—"}
                        {p.document_issue_date ? ` · uitg. ${p.document_issue_date}` : ""}
                        {(p as any).document_expiry_date ? ` · verv. ${(p as any).document_expiry_date}` : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

function PaymentSection({
  label, amount, currency, paidAt, url, sessionId, bookingId, kind,
}: {
  label: string;
  amount: number | null;
  currency: string;
  paidAt: string | null;
  url: string | null;
  sessionId: string | null;
  bookingId?: string;
  kind?: "deposit" | "balance";
}) {
  const qc = useQueryClient();
  const markPaid = useServerFn(setManualPaymentStatus);
  const [localPaidAt, setLocalPaidAt] = useState<string | null>(paidAt);
  const markMut = useMutation({
    mutationFn: markPaid,
    onSuccess: (res: { ok: true; paidAt: string | null }) => {
      setLocalPaidAt(res.paidAt);
      qc.invalidateQueries({ queryKey: ["bookings"] });
    },
  });
  const effectivePaidAt = markMut.isSuccess ? localPaidAt : paidAt;
  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex items-center justify-between">
        <div className="font-medium">
          {label} · {amount ? fmtMoney(amount, currency) : "—"}
        </div>
        <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${
          effectivePaidAt
            ? "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200"
            : sessionId
            ? "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200"
            : url
            ? "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"
            : "bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200"
        }`}>
          {effectivePaidAt
            ? `✓ Betaald · ${new Date(effectivePaidAt).toLocaleDateString()}`
            : sessionId
            ? "✗ Niet betaald · checkout gestart"
            : url
            ? "✗ Niet betaald · link verzonden"
            : "✗ Niet betaald · geen link"}
        </span>
      </div>
      {bookingId && kind && (
        <label className="mt-3 flex items-center gap-2 rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs">
          <input
            type="checkbox"
            className="h-4 w-4 accent-primary"
            checked={!!effectivePaidAt}
            disabled={markMut.isPending}
            onChange={(e) =>
              markMut.mutate({ data: { bookingId, kind, paid: e.target.checked } })
            }
          />
          <span>
            Handmatig als betaald markeren (bv. via overschrijving)
            {markMut.isPending && " · opslaan…"}
          </span>
        </label>
      )}
      {markMut.isError && (
        <p className="mt-1 text-xs text-destructive">{(markMut.error as Error).message}</p>
      )}
      {url ? (
        <div className="mt-3">
          <LinkRow label="Betaallink" url={url} bookingId={bookingId} kind={kind} paid={!!effectivePaidAt} />
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">Nog geen betaallink aangemaakt.</p>
      )}
      {sessionId && (
        <div className="mt-3 text-xs">
          <span className="text-muted-foreground">Stripe-sessie: </span>
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">{sessionId}</code>
        </div>
      )}
    </div>
  );
}

function EventRow({ ev }: { ev: PaymentEvent }) {
  return (
    <li className="grid grid-cols-[1fr_auto] gap-2 bg-card px-3 py-2 text-xs">
      <div className="min-w-0">
        <div className="truncate font-medium">{ev.event_type}</div>
        <div className="truncate text-muted-foreground">
          {ev.payment_kind ? `${ev.payment_kind} · ` : ""}
          {ev.payment_status ?? "—"}
          {ev.amount_cents != null && ev.currency
            ? ` · ${fmtMoney(ev.amount_cents, ev.currency)}`
            : ""}
          {ev.stripe_session_id ? ` · ${ev.stripe_session_id.slice(0, 18)}…` : ""}
        </div>
      </div>
      <div className="text-right text-muted-foreground whitespace-nowrap">
        {new Date(ev.created_at).toLocaleString()}
      </div>
    </li>
  );
}
function ForceMailDelivery({ onDone }: { onDone: () => void }) {
  const flush = useServerFn(forceEmailQueueFlush);
  const [state, setState] = useState<"idle" | "running">("idle");
  const [result, setResult] = useState<ForceFlushResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
      <div className="min-w-[200px] flex-1">
        <p className="text-sm font-medium">Mailbezorging forceren</p>
        <p className="text-xs text-muted-foreground">
          Verwerkt onmiddellijk alle e-mails die nog in de wachtrij staan, zonder te wachten op de
          automatische verzendtaak.
        </p>
      </div>
      <button
        type="button"
        disabled={state === "running"}
        onClick={async () => {
          setState("running");
          setError(null);
          setResult(null);
          try {
            const r = await flush();
            setResult(r);
            if (r.error) toast.error("Mailbezorging mislukt", { description: r.error });
            else if (r.skipped)
              toast.warning("Verzending tijdelijk geblokkeerd", { description: r.skipped });
            else
              toast.success(
                r.processed === 0
                  ? "Geen mails in de wachtrij"
                  : `${r.processed} mail${r.processed === 1 ? "" : "s"} verzonden`,
              );
            onDone();
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Forceren mislukt";
            setError(msg);
            toast.error("Mailbezorging mislukt", { description: msg });
          } finally {
            setState("idle");
          }
        }}
        className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
      >
        {state === "running" ? "Bezig met verzenden…" : "Nu versturen"}
      </button>
      {error && <p className="w-full text-xs text-destructive">{error}</p>}
      {result && (
        <div className="w-full text-xs text-muted-foreground">
          {result.error ? (
            <span className="text-destructive">Fout: {result.error}</span>
          ) : result.skipped ? (
            <span>Verzending tijdelijk geblokkeerd ({result.skipped}) — probeer straks opnieuw.</span>
          ) : (
            <span>
              {result.processed} mail{result.processed === 1 ? "" : "s"} verwerkt in {result.rounds}{" "}
              ronde{result.rounds === 1 ? "" : "s"}.
              {result.processed === 0 ? " De wachtrij was al leeg." : ""}
            </span>
          )}
          {result.recent.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {result.recent.slice(0, 5).map((r, i) => (
                <li key={i}>
                  {new Date(r.created_at).toLocaleString("nl-BE")} · {r.template_name ?? "—"} ·{" "}
                  {r.recipient_email ?? "—"} · {r.status ?? "—"}
                  {r.error_message ? ` · ${r.error_message}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function TemplateTestMail({ onDone }: { onDone: () => void }) {
  const send = useServerFn(sendAllTemplatesTest);
  const [recipient, setRecipient] = useState("info@casamipajobe.com");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TemplateTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
      <div className="min-w-[200px] flex-1">
        <p className="text-sm font-medium">Alle templates testen</p>
        <p className="text-xs text-muted-foreground">
          Verstuurt elk e-mailtemplate één keer met voorbeeldgegevens naar dit adres.
        </p>
      </div>
      <input
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        className="rounded-full border border-border bg-background px-3 py-2 text-sm"
      />
      <button
        type="button"
        disabled={running}
        onClick={async () => {
          setRunning(true);
          setError(null);
          setResult(null);
          try {
            const r = await send({ data: { recipient } });
            setResult(r);
            if (r.failed > 0)
              toast.error(`${r.failed} mail(s) mislukt`, {
                description: `${r.sent} verzonden naar ${r.recipient}`,
              });
            else toast.success(`${r.sent} mails verzonden naar ${r.recipient}`);
            onDone();
          } catch (err) {
            const msg = err instanceof Error ? err.message : "Verzenden mislukt";
            setError(msg);
            toast.error("Verzenden mislukt", { description: msg });
          } finally {
            setRunning(false);
          }
        }}
        className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
      >
        {running ? "Bezig…" : "Alle templates versturen"}
      </button>
      {error && <p className="w-full text-xs text-destructive">{error}</p>}
      {result && (
        <div className="w-full text-xs text-muted-foreground">
          {result.sent} verstuurd naar {result.recipient}
          {result.failed > 0 ? `, ${result.failed} mislukt` : ""}.
          {result.failed > 0 && (
            <ul className="mt-2 space-y-0.5">
              {result.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.template} className="text-destructive">
                    {r.template} · {r.reason ?? "fout"}
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}


function NotificationsPanel() {
  const fetchAudit = useServerFn(listAdminNotificationAudit);
  const sendTest = useServerFn(sendAdminTestNotification);
  const [testState, setTestState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [testError, setTestError] = useState<string | null>(null);
  const [payTestState, setPayTestState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [payTestError, setPayTestError] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ["admin-notification-audit"],
    queryFn: () => fetchAudit(),
    refetchInterval: 15_000,
  });
  const [channel, setChannel] = useState<"all" | "email" | "sms">("all");
  const [status, setStatus] = useState<"all" | "queued" | "sent" | "failed" | "suppressed">("all");
  const [event, setEvent] = useState<string>("all");

  const rows: AdminNotificationAuditRow[] = q.data ?? [];
  const events = Array.from(new Set(rows.map((r) => r.event_type))).sort();
  const filtered = rows.filter((r) =>
    (channel === "all" || r.channel === channel) &&
    (status === "all" || r.status === status) &&
    (event === "all" || r.event_type === event),
  );

  const stat = (s: AdminNotificationAuditRow["status"]) =>
    filtered.filter((r) => r.status === s).length;

  const statusColor: Record<AdminNotificationAuditRow["status"], string> = {
    sent: "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200",
    queued: "bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200",
    suppressed: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
    failed: "bg-destructive/15 text-destructive",
  };

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-2xl border border-border/70 bg-card p-4 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <p className="text-sm font-medium">Testmelding versturen</p>
          <p className="text-xs text-muted-foreground">
            Zet een fake "nieuwe boekingsaanvraag" e-mail + sms in de wachtrij naar elke admin.
          </p>
        </div>
        <button
          type="button"
          disabled={testState === "sending"}
          onClick={async () => {
            setTestState("sending");
            setTestError(null);
            try {
              await sendTest({ data: { eventType: "new" } });
              setTestState("sent");
              toast.success("Testmelding verzonden");
              await q.refetch();
            } catch (err) {
              const msg = err instanceof Error ? err.message : "Verzenden mislukt";
              setTestError(msg);
              setTestState("error");
              toast.error("Testmelding mislukt", { description: msg });
            }
          }}
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
        >
          {testState === "sending" ? "Verzenden…" : testState === "sent" ? "Verzonden ✓ (opnieuw)" : "Test versturen"}
        </button>
        <button
          type="button"
          disabled={payTestState === "sending"}
          onClick={async () => {
            setPayTestState("sending");
            setPayTestError(null);
            try {
              await sendTest({ data: { eventType: "payment_received" } });
              setPayTestState("sent");
              toast.success("Testmelding betaling verzonden");
              await q.refetch();
            } catch (err) {
              const msg = err instanceof Error ? err.message : "Verzenden mislukt";
              setPayTestError(msg);
              setPayTestState("error");
              toast.error("Testmelding betaling mislukt", { description: msg });
            }
          }}
          className="rounded-full border border-primary px-4 py-2 text-sm font-medium text-primary disabled:opacity-60"
        >
          {payTestState === "sending"
            ? "Verzenden…"
            : payTestState === "sent"
              ? "Verzonden ✓ (opnieuw)"
              : "Testbetaling versturen"}
        </button>
        {testError && <p className="w-full text-xs text-destructive">{testError}</p>}
        {payTestError && <p className="w-full text-xs text-destructive">{payTestError}</p>}
      </div>

      <ForceMailDelivery onDone={() => q.refetch()} />
      <TemplateTestMail onDone={() => q.refetch()} />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Totaal" value={filtered.length} />
        <Stat label="Verzonden" value={stat("sent")} />
        <Stat label="In wachtrij" value={stat("queued")} />
        <Stat label="Mislukt" value={stat("failed")} />
        <Stat label="Onderdrukt" value={stat("suppressed")} />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
        <Pill active={channel === "all"} onClick={() => setChannel("all")}>Alle kanalen</Pill>
        <Pill active={channel === "email"} onClick={() => setChannel("email")}>E-mail</Pill>
        <Pill active={channel === "sms"} onClick={() => setChannel("sms")}>SMS</Pill>
        <span className="mx-1 h-4 w-px bg-border" />
        {(["all", "sent", "queued", "failed", "suppressed"] as const).map((s) => (
          <Pill key={s} active={status === s} onClick={() => setStatus(s)}>
            {notifStatusNL(s)}
          </Pill>
        ))}
        <span className="mx-1 h-4 w-px bg-border" />
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          className="rounded-full border border-border bg-card px-3 py-1 text-xs"
        >
          <option value="all">Alle events</option>
          {events.map((e) => (
            <option key={e} value={e}>{e}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
        {q.isLoading ? (
          <p className="p-8 text-sm text-muted-foreground">Laden…</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-sm text-muted-foreground">Geen meldingen passen bij dit filter.</p>
        ) : (
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Wanneer</th>
                <th className="px-4 py-3">Kanaal</th>
                <th className="px-4 py-3">Ontvanger</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Boeking</th>
                <th className="px-4 py-3">Sjabloon</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase tracking-wider">{r.channel}</td>
                  <td className="px-4 py-3">{r.recipient}</td>
                  <td className="px-4 py-3 text-xs">{r.event_type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.booking_reference ?? "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    <span className="flex items-center gap-2">
                      <AudienceBadge template={r.template_name} />
                      <span>{r.template_name ?? "—"}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColor[r.status]}`}>
                      {notifStatusNL(r.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {r.error_message ? (
                      <span className="text-destructive">{r.error_message}</span>
                    ) : (
                      r.provider_message_id ?? "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card p-4">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 transition ${
        active ? "bg-primary text-primary-foreground" : "border border-border bg-card hover:bg-muted"
      }`}
    >
      {children}
    </button>
  );
}

/* ---------- keykeepers panel ---------- */

function KeykeepersPanel({ bookings }: { bookings: AdminBooking[] }) {
  const qc = useQueryClient();
  const listFn = useServerFn(listKeykeepers);
  const upsertFn = useServerFn(upsertKeykeeper);
  const deleteFn = useServerFn(deleteKeykeeper);

  const listQ = useQuery({ queryKey: ["keykeepers"], queryFn: () => listFn() });

  const upsertMut = useMutation({
    mutationFn: upsertFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keykeepers"] }),
  });
  const deleteMut = useMutation({
    mutationFn: deleteFn,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keykeepers"] }),
  });

  const [editing, setEditing] = useState<Keykeeper | null>(null);
  const [adding, setAdding] = useState(false);
  const [sendFor, setSendFor] = useState<AdminBooking | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = bookings
    .filter((b) => b.status !== "cancelled" && b.check_out >= today)
    .sort((a, b) => a.check_in.localeCompare(b.check_in));

  const keepers = listQ.data ?? [];

  return (
    <section className="space-y-10">
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold">Sleutelbeheer</h2>
            <p className="text-sm text-muted-foreground">
              Personen die boekingsdetails ontvangen om de villa voor te bereiden.
            </p>
          </div>
          <button
            onClick={() => setAdding(true)}
            className="shrink-0 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            + Sleutelbeheerder toevoegen
          </button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
          {listQ.isLoading ? (
            <p className="p-8 text-sm text-muted-foreground">Laden…</p>
          ) : keepers.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground">
              Nog geen sleutelbeheerders. Voeg er één toe om boekingsdetails te sturen.
            </p>
          ) : (
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Naam</th>
                  <th className="px-4 py-3">E-mail</th>
                  <th className="px-4 py-3">Telefoon</th>
                  <th className="px-4 py-3">Actief</th>
                  <th className="px-4 py-3 text-right">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {keepers.map((k) => (
                  <tr key={k.id}>
                    <td className="px-4 py-3 font-medium">{k.name}</td>
                    <td className="px-4 py-3">{k.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{k.phone || "—"}</td>
                    <td className="px-4 py-3">{k.active ? "Ja" : "Nee"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-wrap justify-end gap-1.5 text-xs">
                        <button onClick={() => setEditing(k)} className="rounded border border-border px-2 py-1 hover:bg-muted">Bewerken</button>
                        <button
                          disabled={deleteMut.isPending}
                          onClick={() => { if (confirm(`${k.name} verwijderen?`)) deleteMut.mutate({ data: { id: k.id } }); }}
                          className="rounded border border-destructive/40 px-2 py-1 text-destructive hover:bg-destructive/10"
                        >Wis</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-semibold">Boeking naar sleutelbeheer sturen</h2>
        <p className="text-sm text-muted-foreground">
          Aankomende en actieve boekingen. Verstuurt een e-mail met gast- en verblijfsdetails.
        </p>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-border/70 bg-card shadow-sm">
          {upcoming.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground">Geen aankomende boekingen.</p>
          ) : (
            <table className="w-full min-w-[640px] text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Ref</th>
                  <th className="px-4 py-3">Gast</th>
                  <th className="px-4 py-3">Datums</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Versturen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {upcoming.map((b) => (
                  <tr key={b.id}>
                    <td className="px-4 py-3 font-mono text-xs">{b.reference}</td>
                    <td className="px-4 py-3">{b.name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{b.check_in} → {b.check_out}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{statusNL(b.status)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        disabled={keepers.length === 0}
                        onClick={() => setSendFor(b)}
                        className="rounded border border-primary/40 bg-primary/5 px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                      >
                        Naar sleutelbeheer sturen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(adding || editing) && (
        <KeykeeperDialog
          keykeeper={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSubmit={(payload) =>
            upsertMut.mutateAsync({ data: payload }).then(() => {
              setAdding(false);
              setEditing(null);
            })
          }
          submitting={upsertMut.isPending}
        />
      )}
      {sendFor && (
        <SendKeykeeperDialog
          booking={sendFor}
          keepers={keepers.filter((k) => k.active)}
          onClose={() => setSendFor(null)}
        />
      )}
    </section>
  );
}

function KeykeeperDialog({
  keykeeper,
  onClose,
  onSubmit,
  submitting,
}: {
  keykeeper: Keykeeper | null;
  onClose: () => void;
  onSubmit: (data: {
    id?: string;
    name: string;
    email: string;
    phone: string;
    notes: string;
    active: boolean;
  }) => Promise<unknown>;
  submitting: boolean;
}) {
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const p = Object.fromEntries(fd.entries()) as Record<string, string>;
    onSubmit({
      id: keykeeper?.id,
      name: p.name,
      email: p.email,
      phone: p.phone ?? "",
      notes: p.notes ?? "",
      active: p.active === "on",
    });
  }

  return (
    <DialogShell title={keykeeper ? `${keykeeper.name} bewerken` : "Sleutelbeheerder toevoegen"} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input name="name" label="Naam" defaultValue={keykeeper?.name} required />
        <Input name="email" label="E-mail" type="email" defaultValue={keykeeper?.email} required />
        <Input name="phone" label="Telefoon" defaultValue={keykeeper?.phone ?? ""} />
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Notities (intern)</span>
          <textarea
            name="notes"
            defaultValue={keykeeper?.notes ?? ""}
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" name="active" defaultChecked={keykeeper?.active ?? true} />
          Actief (ontvangt boekingsmails)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border border-border px-4 py-1.5 text-sm hover:bg-muted">Annuleren</button>
          <button type="submit" disabled={submitting} className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {submitting ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function SendKeykeeperDialog({
  booking,
  keepers,
  onClose,
}: {
  booking: AdminBooking;
  keepers: Keykeeper[];
  onClose: () => void;
}) {
  const sendFn = useServerFn(sendBookingToKeykeepers);
  const [selected, setSelected] = useState<Set<string>>(
    new Set(keepers.map((k) => k.id)),
  );
  const [notes, setNotes] = useState("");
  const mut = useMutation({
    mutationFn: sendFn,
    onSuccess: (res) => {
      if (res.ok) toast.success("Mail verzonden naar sleutelbeheer");
      else toast.error("Sommige mails konden niet verzonden worden");
    },
    onError: (e: unknown) =>
      toast.error("Mail verzenden mislukt", {
        description: e instanceof Error ? e.message : undefined,
      }),
  });

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (selected.size === 0) return;
    mut.mutate({
      data: {
        bookingId: booking.id,
        keykeeperIds: Array.from(selected),
        notes,
      },
    });
  }

  const done = mut.data;

  return (
    <DialogShell title={`${booking.reference} naar sleutelbeheer sturen`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          <div><span className="font-medium text-foreground">{booking.name}</span> · {booking.check_in} → {booking.check_out}</div>
          <div className="mt-0.5">{booking.adults}V {booking.children}K {booking.babies}B · {booking.bedrooms} slaapkamer(s)</div>
        </div>

        {keepers.length === 0 ? (
          <p className="text-sm text-muted-foreground">Geen actieve sleutelbeheerders. Voeg er eerst één toe.</p>
        ) : (
          <div className="space-y-2">
            <span className="text-xs font-medium text-muted-foreground">Ontvangers</span>
            {keepers.map((k) => (
              <label key={k.id} className="flex items-center gap-2 rounded border border-border px-3 py-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.has(k.id)}
                  onChange={() => toggle(k.id)}
                />
                <span className="font-medium">{k.name}</span>
                <span className="text-xs text-muted-foreground">· {k.email}</span>
              </label>
            ))}
          </div>
        )}

        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Extra notities voor sleutelbeheer (optioneel)</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
            placeholder="bv. Leg 4 sets handdoeken klaar, vul het welkomstmandje aan…"
          />
        </label>

        {done ? (
          <div className={`rounded-md p-3 text-xs ${done.ok ? "bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-200" : "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"}`}>
            {done.ok ? "Verzonden naar alle geselecteerde sleutelbeheerders." : "Sommige e-mails konden niet worden verzonden:"}
            {!done.ok && (
              <ul className="mt-1 list-disc pl-5">
                {done.results.filter((r) => !r.ok).map((r) => (
                  <li key={r.id}>{r.email}: {r.reason ?? "mislukt"}</li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border border-border px-4 py-1.5 text-sm hover:bg-muted">
            {done?.ok ? "Sluiten" : "Annuleren"}
          </button>
          {!done?.ok && (
            <button
              type="submit"
              disabled={mut.isPending || selected.size === 0 || keepers.length === 0}
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {mut.isPending ? "Verzenden…" : `Versturen naar ${selected.size}`}
            </button>
          )}
        </div>
      </form>
    </DialogShell>
  );
}
